import usePageFilterOptions from "../hooks/usePageFilterOptions";
import CompanyTableCell, { PipelineCompanyTableCell } from "../components/CompanyTableCell";
import { openInterviewScheduler } from "../utils/interviewScheduling";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
// JobCandidatesTable.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { CANDIDATE_EXPORT_TAB_FIELDS } from "../utils/exportTabFields";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useAuthController from "../hooks/useAuthController";
import {
  Box,
  Typography,
  TableCell,
  Avatar,
  Alert,
  Divider,
  Button,
  Chip,
  LinearProgress,
  Stack,
  CircularProgress,
  Card,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Snackbar,
  Dialog,
  Tab,
  Tabs,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  User,
  Filter,
} from "iconsax-react";

import Breadcrumb from "../components/Breadcrumb";
import StatsSlider from "../components/StatsSlider";
import { CORE_CANDIDATE_STAGES, RECRUITING_STAGES, coreCandidateStage, pipelineStageKey, stageColor, recruitingMetricColors, recruitingStageColors } from "../utils/pipelineStages";
import RecruitingPipelineTable from "../components/RecruitingPipelineTable";
import { CandidateRecruitingActions } from "../components/RecruitingNextActions";
import TopListHeaderBar from "../components/TopListHeaderBar";
import BulkActionsBar from "../components/BulkActionsBar";
import AssignTeamMemberAction from "../components/AssignTeamMemberAction";
import CandidateTabsLayout from "../components/CandidateTabsLayout";
import ReusableRightDrawer from "../components/ReusableRightDrawer";
import InfoActionIcons from "../components/InfoActionIcons";
import TableLocationValue from "../components/TableLocationValue";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import { AgentActionIcon } from "../components/ActionButtonIcons";
import AddToSequenceDialog from "../components/PopupWindows/AddToSequenceDialog.jsx";
import AddCandidateDialog from "../components/PopupWindows/AddCandidateDialog.jsx";
import CsvImportDialog from "../components/CsvImportDialog";
import useCsvImport from "../hooks/useCsvImport";
import AIAgentDialog from "../components/PopupWindows/AIAgentDialog.jsx";
import CandidatePitchPackageDialog from "../components/PopupWindows/CandidatePitchPackageDialog.jsx";
import { buildCandidateSequenceItem } from "../utils/sequenceItems";
import { formatRelativeTimeShort } from "../utils/relativeTime";
import {
  createSidebarResetState,
  getSidebarResetToken,
} from "../utils/sidebarNavigation";
import {
  dedupeHiringManagerRows,
  fetchHiringManagers,
  isHiringManagerContactRow,
} from "../utils/hiringManagers";
import {
  groupLocationOption,
  normalizeLocationFilterValues,
} from "../utils/locationFilters";
import PopupMultiSelectField, { popupFilterInputSx } from "../components/PopupMultiSelectField";
import LocationMultiSelectField from "../components/LocationMultiSelectField";
import CandidateAdvancedSearchView from "../components/CandidateAdvancedSearchView";
import {
  buildPublicSharePayload,
  createAndCopyPublicShareLink,
  SHARE_LINK_COPIED_MESSAGE,
} from "../utils/publicShareLinks";
import { CANDIDATE_STATUS_VALUES, CANDIDATE_STATUS_STATS } from "../utils/candidateStatusOptions";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const PURPLE = "#9C4FFF";
const BORDER = "#EAECF0";
const TEXT = "#344054";
const MUTED = "#98A2B3";
const BULK_ACTION_RIGHT_BUTTON_SX = {
  background: `${PURPLE} !important`,
  backgroundColor: `${PURPLE} !important`,
  color: "#fff !important",
  borderColor: `${PURPLE} !important`,
  "&:hover": {
    background: `${PURPLE} !important`,
    backgroundColor: `${PURPLE} !important`,
    color: "#fff !important",
    borderColor: `${PURPLE} !important`,
  },
  "&:active": {
    background: `${PURPLE} !important`,
    backgroundColor: `${PURPLE} !important`,
    color: "#fff !important",
    borderColor: `${PURPLE} !important`,
  },
  "&:focus-visible, &.Mui-focusVisible": {
    background: `${PURPLE} !important`,
    backgroundColor: `${PURPLE} !important`,
    color: "#fff !important",
    borderColor: `${PURPLE} !important`,
    outline: `3px solid ${PURPLE}`,
  },
};
const HEADER_ACTION_BUTTON_SX = {
  ...BULK_ACTION_RIGHT_BUTTON_SX,
  minWidth: "auto",
  height: 36,
  px: 1.75,
  borderRadius: "999px",
  textTransform: "none",
  fontSize: 12,
  fontWeight: 500,
  boxShadow: "none",
};
const AGENT_TASK_CANDIDATE_LIMIT = 25;
const CANDIDATE_AGENT_TASK_OPTIONS = [
  { value: "enrich-profile", label: "Enrich Candidate Profile" },
  { value: "build-pitch-package", label: "Build Pitch Package" },
  { value: "build-sales-sequence", label: "Build Sales Sequence" },
  { value: "find-job-leads", label: "Find Job Leads for Candidate" },
];
const BLUE = "#3451F4";
const EMPTY_VALUE = "-";

const normalizeTab = (t) => {
  const v = String(t || "").toLowerCase();
  const ok = ["details", "stages", "activities"];
  return ok.includes(v) ? v : "details";
};

const normalizeApiBase = (base) => String(base || "").replace(/\/$/, "");

const getToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token");

  if (direct && String(direct).trim()) return String(direct).trim();

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      const t =
        obj?.token ||
        obj?.accessToken ||
        obj?.access_token ||
        obj?.jwt ||
        obj?.idToken ||
        obj?.id_token;
      if (t && String(t).trim()) return String(t).trim();
    } catch {}
  }

  return "";
};

const authHeaders = () => {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(token ? { "x-access-token": token } : {}),
  };
};

const pickFirst = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length) return value;
      continue;
    }

    return value;
  }
  return "";
};

const extractRows = (data) => {
  if (data && Array.isArray(data.rows)) return data.rows;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.candidates)) return data.candidates;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && Array.isArray(data.companies)) return data.companies;
  if (Array.isArray(data) && Array.isArray(data[0])) return data[0];
  if (Array.isArray(data)) return data;
  return [];
};

const safeJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const safeJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const asArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((x) => String(x).trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const parsed = safeJsonArray(value);
    if (parsed.length) {
      return parsed
        .filter(Boolean)
        .map((x) => String(x).trim())
        .filter(Boolean);
    }

    return value
      .split(",")
      .map((x) => String(x).trim())
      .filter(Boolean);
  }

  return [];
};

const uniqueSorted = (arr) =>
  Array.from(
    new Set(
      (arr || [])
        .filter(Boolean)
        .map((x) => String(x).trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

const normalizeStatusKey = (value) => String(value || "").trim().toLowerCase();
const normalizeStageKey = (value) => String(value || "").trim().toLowerCase();
// Candidate stages are canonicalized by the API. "Interviewing" is retained
// only as a legacy input alias and must be presented as the canonical "Interview".
const normalizeCandidateStageFilterValue = (value) => {
  const stage = String(value || "").trim();
  return normalizeStageKey(stage) === "interviewing" ? "Interview" : stage;
};
const cleanText = (value) => String(value ?? "").trim();
const normalizeFilterTerm = (value) => String(value ?? "").trim().toLowerCase();
const removeFilterValueFromList = (values = [], target) =>
  uniqueSorted(
    (values || []).filter(
      (value) => normalizeFilterTerm(value) !== normalizeFilterTerm(target)
    )
  );

const matchesAnySelectedValue = (selected = [], values = []) => {
  const selectedTerms = uniqueSorted(selected).map(normalizeFilterTerm).filter(Boolean);
  if (!selectedTerms.length) return true;

  const rowValues = values
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  return selectedTerms.some((term) => rowValues.some((value) => value.includes(term)));
};

const normalizeAdvancedList = (value) => uniqueSorted(Array.isArray(value) ? value : value ? [value] : []);
const normalizeLocationList = (value) =>
  normalizeLocationFilterValues(Array.isArray(value) ? value : value ? [value] : []);

const matchesTextQuery = (value, query) => {
  const term = normalizeFilterTerm(query);
  if (!term) return true;
  return String(value ?? "").toLowerCase().includes(term);
};

const stringifyCandidatePreferences = (value) => {
  if (typeof value === "string") {
    return value.trim() ? value : "{}";
  }

  try {
    return JSON.stringify(value && typeof value === "object" && !Array.isArray(value) ? value : {});
  } catch {
    return "{}";
  }
};

const getCandidatePreferences = (row = null) => safeJsonObject(row?.preferences_json || row?.preferences);
const getCandidateExperienceItems = (row = null) => safeJsonArray(row?.experience_items || row?.experience);
const getCandidateEducationItems = (row = null) => safeJsonArray(row?.education_items || row?.education);

const getCandidateNameParts = (row = null) => {
  const parts = cleanText(row?.full_name || row?.candidate_name || row?.name)
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName: parts[0] || "",
    lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
  };
};

const extractCandidateLocations = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return normalizeLocationFilterValues([
    row?.candidate_location,
    row?.location,
    row?.current_location,
    ...asArray(row?.pref_locations),
    ...asArray(preferences.locations),
    ...asArray(preferences.location),
    ...asArray(preferences.preferred_locations),
    ...asArray(preferences.preferredLocations),
  ]);
};

const extractCandidateLanguages = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    ...asArray(preferences.languages),
    ...asArray(preferences.language),
    ...asArray(preferences.spoken_languages),
    ...asArray(preferences.spokenLanguages),
    ...asArray(preferences.language_list),
    ...asArray(preferences.languageList),
    ...asArray(preferences.languages_spoken),
  ]);
};

const extractCandidateIndustries = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    row?.company_industry,
    row?.industry,
    row?.industry_sector,
    preferences.industry,
    preferences.company_industry,
    ...asArray(preferences.industries),
    ...asArray(preferences.companyIndustries),
  ]);
};

const extractCandidateSchools = (row = null) =>
  uniqueSorted(
    getCandidateEducationItems(row).map((item) => pickFirst(item?.school, item?.institution))
  );

const extractCandidateFieldsOfStudy = (row = null) =>
  uniqueSorted(
    getCandidateEducationItems(row).flatMap((item) => [
      pickFirst(item?.field, item?.major),
      item?.degree,
    ])
  );

const toExperienceDateScore = (value) => {
  const raw = cleanText(value);
  if (!raw) return Number.NEGATIVE_INFINITY;
  if (/current|present/i.test(raw)) return Date.now();

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

  const yearMatch = raw.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) {
    const fallbackDate = new Date(Number(yearMatch[0]), 0, 1);
    if (!Number.isNaN(fallbackDate.getTime())) return fallbackDate.getTime();
  }

  return Number.NEGATIVE_INFINITY;
};

const getOrderedExperienceCompanies = (row = null) => {
  const seen = new Set();

  return getCandidateExperienceItems(row)
    .map((item) => ({
      company: pickFirst(item?.company, item?.employer),
      from: item?.from || item?.start || item?.startDate,
      to: item?.to || item?.end || item?.endDate,
    }))
    .filter((item) => cleanText(item.company))
    .sort((a, b) => {
      const currentDelta =
        Number(/current|present/i.test(cleanText(b.to))) -
        Number(/current|present/i.test(cleanText(a.to)));
      if (currentDelta) return currentDelta;

      const endDelta = toExperienceDateScore(b.to) - toExperienceDateScore(a.to);
      if (endDelta) return endDelta;

      return toExperienceDateScore(b.from) - toExperienceDateScore(a.from);
    })
    .map((item) => item.company)
    .filter((company) => {
      const key = normalizeFilterTerm(company);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const extractCurrentCompany = (row = null) => {
  const preferences = getCandidatePreferences(row);
  const orderedExperienceCompanies = getOrderedExperienceCompanies(row);

  return pickFirst(
    row?.candidate_company,
    row?.company,
    row?.current_company,
    row?.currentCompany,
    row?.company_client,
    row?.company_name,
    row?.candidate_company_name,
    preferences.current_company,
    preferences.currentCompany,
    preferences.company_client,
    orderedExperienceCompanies[0]
  );
};

const extractPastCompanies = (row = null) => {
  const currentCompany = normalizeFilterTerm(extractCurrentCompany(row));
  return getOrderedExperienceCompanies(row).filter(
    (company) => normalizeFilterTerm(company) !== currentCompany
  );
};

const extractCandidateCompanySize = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return pickFirst(
    row?.company_size,
    row?.size_range,
    row?.companySize,
    preferences.company_size,
    preferences.companySize,
    preferences.current_company_size,
    preferences.currentCompanySize
  );
};

const extractCandidateCompanyTypes = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    row?.company_type,
    row?.companyType,
    row?.ownership_type,
    row?.ownershipType,
    preferences.company_type,
    preferences.companyType,
    ...asArray(preferences.company_types),
    ...asArray(preferences.companyTypes),
    ...asArray(preferences.ownership_type),
    ...asArray(preferences.ownershipType),
  ]);
};

const extractCandidateJobFunctions = (row = null) =>
  uniqueSorted([
    row?.title,
    row?.candidate_title,
    row?.job_title,
    ...getCandidateExperienceItems(row).map((item) => pickFirst(item?.jobTitle, item?.title, item?.position)),
  ]);

const extractCandidateRecruitmentActivities = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    row?.candidate_stage,
    row?.stage,
    row?.candidate_status,
    row?.status,
    row?.source,
    row?.last_sync_label,
    preferences.timeline,
    preferences.availability,
  ]);
};

const extractCandidateExplicitTags = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    ...asArray(row?.tags),
    ...asArray(row?.candidate_tags),
    ...asArray(row?.tag_list),
    ...asArray(row?.tagList),
    ...asArray(preferences.tags),
    ...asArray(preferences.tagList),
    ...asArray(preferences.shortlist_tags),
    ...asArray(preferences.shortlistTags),
  ]);
};

const extractCandidateTags = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    ...extractCandidateExplicitTags(row),
    ...parseSkillsFromAny(row),
    ...asArray(preferences.jobTypes),
    ...asArray(preferences.workModes),
    preferences.jobType,
    preferences.workMode,
  ]);
};

const extractCandidateJobItems = (row = null) => {
  const location = pickFirst(row?.candidate_location, row?.location, row?.current_location);
  const explicit = safeJsonArray(
    row?.jobs_json ||
      row?.job_titles_json ||
      row?.open_roles_json ||
      row?.jobs_list_json ||
      row?.matched_jobs_json ||
      row?.candidate_jobs_json
  );

  const normalizedExplicit = explicit
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") return { title: cleanText(item), location };
      if (typeof item !== "object") return null;

      return {
        title: pickFirst(item?.title, item?.job_title, item?.name, item?.label),
        location: pickFirst(item?.location, item?.city, item?.country, location),
      };
    })
    .filter((item) => cleanText(item?.title));

  if (normalizedExplicit.length) return normalizedExplicit;

  const singleTitle = pickFirst(
    row?.job_title,
    row?.lead_job_title,
    row?.matched_job_title,
    row?.requisition_title,
    row?.role_title,
    row?.position_title,
    row?.job_name
  );
  if (singleTitle) return [{ title: singleTitle, location }];

  const textList = uniqueSorted([
    ...asArray(row?.job_titles),
    ...asArray(row?.open_roles),
    ...asArray(row?.jobs_list),
    ...asArray(row?.job_list),
    ...asArray(row?.matched_jobs),
    ...asArray(row?.candidate_jobs),
  ]);
  if (textList.length) {
    return textList.map((title) => ({ title, location }));
  }

  const openings = Number(row?.job_openings || 0);
  if (!Number.isFinite(openings) || openings <= 0) return [];

  return [{ title: openings === 1 ? "1 open role" : `${openings} open roles`, location }];
};

const buildCandidateJobsSummary = (row = null) =>
  uniqueSorted(
    extractCandidateJobItems(row).map((item) => pickFirst(item?.title, item?.job_title, item?.name))
  );

const extractCandidatePastApplications = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    row?.source,
    row?.last_sync_label,
    row?.company,
    row?.candidate_company,
    ...extractPastCompanies(row),
    ...asArray(preferences.past_applications),
    ...asArray(preferences.pastApplications),
  ]);
};

const extractCandidateNoteValues = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return uniqueSorted([
    row?.notes,
    row?.pref_why_looking,
    preferences.notes,
    preferences.whyLooking,
  ]);
};

const extractCandidateLastActivityAt = (row = null) =>
  pickFirst(
    row?.activities?.[0]?.created_at,
    row?.activities?.[0]?.createdAt,
    row?.activities?.[0]?.ts,
    row?.last_activity_at,
    row?.last_activity_time,
    row?.last_activity_date,
    row?.lastActivityAt,
    row?.lastActivityTime,
    row?.lastActivityDate,
    row?.created_at,
    row?.createdAt
  );

const extractCandidateLastActivitySummary = (row = null) => {
  const preferences = getCandidatePreferences(row);
  return pickFirst(
    row?.activities?.[0]?.details,
    row?.activities?.[0]?.summary,
    row?.activities?.[0]?.title,
    row?.last_activity_detail,
    row?.last_activity_message,
    row?.last_sync_label,
    row?.source,
    row?.notes,
    preferences.notes,
    preferences.whyLooking
  );
};

const parsePossibleDate = (value) => {
  const raw = cleanText(value);
  if (!raw) return null;
  if (/current|present/i.test(raw)) return new Date();

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const yearMatch = raw.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) {
    const fallbackDate = new Date(Number(yearMatch[0]), 0, 1);
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }

  return null;
};

const diffInYears = (start, end = new Date()) => {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return null;
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, Number(((end.getTime() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000)).toFixed(1)));
};

const estimateYearsOfExperience = (row = null) => {
  const ranges = getCandidateExperienceItems(row)
    .map((item) => ({
      start: parsePossibleDate(item?.from || item?.start || item?.startDate),
      end: parsePossibleDate(item?.to || item?.end || item?.endDate) || new Date(),
    }))
    .filter((range) => range.start);

  if (!ranges.length) return null;

  const earliestStart = ranges.reduce(
    (min, range) => (range.start < min ? range.start : min),
    ranges[0].start
  );
  const latestEnd = ranges.reduce(
    (max, range) => (range.end > max ? range.end : max),
    ranges[0].end
  );

  return diffInYears(earliestStart, latestEnd);
};

const estimateYearsAtCurrentCompany = (row = null) => {
  const currentExperience = getCandidateExperienceItems(row).find((item) => {
    const endValue = cleanText(item?.to || item?.end || item?.endDate);
    return !endValue || /current|present/i.test(endValue);
  });

  if (!currentExperience) return null;

  const startDate = parsePossibleDate(
    currentExperience?.from || currentExperience?.start || currentExperience?.startDate
  );

  return diffInYears(startDate, new Date());
};

const matchesYearsFilter = (value, rawFilter) => {
  const query = cleanText(rawFilter);
  if (!query) return true;
  if (!Number.isFinite(value)) return false;

  const numericMatch = query.match(/\d+(\.\d+)?/);
  if (numericMatch) return value >= Number(numericMatch[0]);

  return String(value).toLowerCase().includes(query.toLowerCase());
};

const createEmptyCandidateAdvancedFilters = () => ({
  aiAgent: "",
  jobTitle: [],
  skills: [],
  locations: [],
  owner: [],
  status: [],
  stage: [],
  source: [],
  candidate_details: {
    full_names: [],
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    spoken_languages: [],
  },
  education_experience: {
    years_experience: "",
    years_current_company: "",
    spoken_languages: [],
    industry: [],
    schools: [],
    fields_of_study: [],
  },
  company: {
    current_company: "",
    company_size: "",
    past_companies: [],
    company_types: [],
    job_functions: [],
  },
  activity_section: {
    recruitment_activity: [],
    tags: [],
    past_applications: [],
    notes: [],
  },
});

const normalizeCandidateAdvancedFilters = (input) => {
  const empty = createEmptyCandidateAdvancedFilters();
  const next = input || {};
  const candidateDetails = next.candidate_details || {};
  const educationExperience = next.education_experience || {};
  const company = next.company || {};
  const activitySection = next.activity_section || {};

  return {
    aiAgent: cleanText(next.aiAgent),
    jobTitle: normalizeAdvancedList(next.jobTitle),
    skills: normalizeAdvancedList(next.skills),
    locations: normalizeLocationList(next.locations),
    owner: normalizeAdvancedList(next.owner),
    status: normalizeAdvancedList(next.status),
    stage: normalizeAdvancedList(next.stage),
    source: normalizeAdvancedList(next.source),
    candidate_details: {
      ...empty.candidate_details,
      full_names: normalizeAdvancedList(candidateDetails.full_names),
      first_name: cleanText(candidateDetails.first_name),
      last_name: cleanText(candidateDetails.last_name),
      email: cleanText(candidateDetails.email),
      phone: cleanText(candidateDetails.phone),
      spoken_languages: normalizeAdvancedList(candidateDetails.spoken_languages),
    },
    education_experience: {
      ...empty.education_experience,
      years_experience: cleanText(educationExperience.years_experience),
      years_current_company: cleanText(educationExperience.years_current_company),
      spoken_languages: normalizeAdvancedList(educationExperience.spoken_languages),
      industry: normalizeAdvancedList(educationExperience.industry),
      schools: normalizeAdvancedList(educationExperience.schools),
      fields_of_study: normalizeAdvancedList(educationExperience.fields_of_study),
    },
    company: {
      ...empty.company,
      current_company: cleanText(company.current_company),
      company_size: cleanText(company.company_size),
      past_companies: normalizeAdvancedList(company.past_companies),
      company_types: normalizeAdvancedList(company.company_types),
      job_functions: normalizeAdvancedList(company.job_functions),
    },
    activity_section: {
      ...empty.activity_section,
      recruitment_activity: normalizeAdvancedList(activitySection.recruitment_activity),
      tags: normalizeAdvancedList(activitySection.tags),
      past_applications: normalizeAdvancedList(activitySection.past_applications),
      notes: normalizeAdvancedList(activitySection.notes),
    },
  };
};

const mergeBasicIntoAdvancedCandidateFilters = (basicFilters, currentAdvanced) => {
  const base = normalizeCandidateAdvancedFilters(currentAdvanced);
  const nextBasic = basicFilters || {};

  return {
    ...base,
    aiAgent: cleanText(nextBasic.aiRequest),
    skills: normalizeAdvancedList(nextBasic.skills),
    locations: normalizeLocationList(nextBasic.locations),
    owner: normalizeAdvancedList(nextBasic.owners),
    status: normalizeAdvancedList(nextBasic.statuses),
    stage: normalizeAdvancedList(nextBasic.stages),
    source: normalizeAdvancedList(nextBasic.sources),
    candidate_details: {
      ...base.candidate_details,
      full_names: normalizeAdvancedList(nextBasic.names),
    },
  };
};

const advancedCandidateFiltersToBasicFilters = (advancedFilters, currentBasic) => {
  const base = currentBasic || createEmptyFilterDraft();
  const advanced = normalizeCandidateAdvancedFilters(advancedFilters);

  return {
    ...base,
    aiRequest: advanced.aiAgent,
    names: normalizeAdvancedList(advanced.candidate_details.full_names),
    skills: normalizeAdvancedList(advanced.skills),
    locations: normalizeLocationList(advanced.locations),
    owners: normalizeAdvancedList(advanced.owner),
    statuses: normalizeAdvancedList(advanced.status),
    stages: normalizeAdvancedList(advanced.stage),
    sources: normalizeAdvancedList(advanced.source),
  };
};

const applyAdvancedCandidateFilters = (rows = [], advancedFilters) => {
  if (!advancedFilters) return rows;

  const advanced = normalizeCandidateAdvancedFilters(advancedFilters);

  return rows.filter((row) => {
    const nameParts = getCandidateNameParts(row);
    const emailValue = getEmailValue(row);
    const phoneValue = pickFirst(row?.phone, row?.phone_number, row?.mobile);
    const locations = extractCandidateLocations(row);
    const spokenLanguages = extractCandidateLanguages(row);
    const industries = extractCandidateIndustries(row);
    const schools = extractCandidateSchools(row);
    const fieldsOfStudy = extractCandidateFieldsOfStudy(row);
    const currentCompany = extractCurrentCompany(row);
    const pastCompanies = extractPastCompanies(row);
    const companySize = extractCandidateCompanySize(row);
    const companyTypes = extractCandidateCompanyTypes(row);
    const jobFunctions = extractCandidateJobFunctions(row);
    const recruitmentActivities = extractCandidateRecruitmentActivities(row);
    const tags = extractCandidateTags(row);
    const pastApplications = extractCandidatePastApplications(row);
    const noteValues = extractCandidateNoteValues(row);
    const aiAgentSearchValues = uniqueSorted([
      row?.full_name,
      row?.title,
      emailValue,
      phoneValue,
      ...jobFunctions,
      ...locations,
      ...spokenLanguages,
      ...industries,
      ...schools,
      ...fieldsOfStudy,
      currentCompany,
      companySize,
      ...pastCompanies,
      ...companyTypes,
      ...recruitmentActivities,
      ...tags,
      ...pastApplications,
      ...noteValues,
    ]);

    if (advanced.aiAgent && !matchesAnySelectedValue([advanced.aiAgent], aiAgentSearchValues)) {
      return false;
    }

    if (advanced.jobTitle.length && !matchesAnySelectedValue(advanced.jobTitle, jobFunctions)) return false;
    if (advanced.skills.length && !matchesAnySelectedValue(advanced.skills, parseSkillsFromAny(row))) return false;
    if (advanced.locations.length && !matchesAnySelectedValue(advanced.locations, locations)) return false;
    if (advanced.owner.length && !matchesAnySelectedValue(advanced.owner, [row?.recruiter_name || row?.owner])) return false;
    if (advanced.status.length && !matchesAnySelectedValue(advanced.status, [row?.candidate_status || row?.status])) return false;
    if (advanced.stage.length && !matchesAnySelectedValue(advanced.stage, [row?.candidate_stage || row?.stage])) return false;
    if (advanced.source.length && !matchesAnySelectedValue(advanced.source, [row?.source || row?.last_sync_label])) return false;

    if (advanced.candidate_details.full_names.length && !matchesAnySelectedValue(advanced.candidate_details.full_names, [row?.full_name])) {
      return false;
    }
    if (!matchesTextQuery(nameParts.firstName, advanced.candidate_details.first_name)) return false;
    if (!matchesTextQuery(nameParts.lastName, advanced.candidate_details.last_name)) return false;
    if (!matchesTextQuery(emailValue, advanced.candidate_details.email)) return false;
    if (!matchesTextQuery(phoneValue, advanced.candidate_details.phone)) return false;
    if (
      advanced.candidate_details.spoken_languages.length &&
      !matchesAnySelectedValue(advanced.candidate_details.spoken_languages, spokenLanguages)
    ) {
      return false;
    }

    if (!matchesYearsFilter(estimateYearsOfExperience(row), advanced.education_experience.years_experience)) {
      return false;
    }
    if (
      !matchesYearsFilter(
        estimateYearsAtCurrentCompany(row),
        advanced.education_experience.years_current_company
      )
    ) {
      return false;
    }
    if (
      advanced.education_experience.spoken_languages.length &&
      !matchesAnySelectedValue(advanced.education_experience.spoken_languages, spokenLanguages)
    ) {
      return false;
    }
    if (
      advanced.education_experience.industry.length &&
      !matchesAnySelectedValue(advanced.education_experience.industry, industries)
    ) {
      return false;
    }
    if (
      advanced.education_experience.schools.length &&
      !matchesAnySelectedValue(advanced.education_experience.schools, schools)
    ) {
      return false;
    }
    if (
      advanced.education_experience.fields_of_study.length &&
      !matchesAnySelectedValue(advanced.education_experience.fields_of_study, fieldsOfStudy)
    ) {
      return false;
    }

    if (!matchesTextQuery(currentCompany, advanced.company.current_company)) return false;
    if (!matchesTextQuery(companySize, advanced.company.company_size)) return false;
    if (
      advanced.company.past_companies.length &&
      !matchesAnySelectedValue(advanced.company.past_companies, pastCompanies)
    ) {
      return false;
    }
    if (
      advanced.company.company_types.length &&
      !matchesAnySelectedValue(advanced.company.company_types, companyTypes)
    ) {
      return false;
    }
    if (
      advanced.company.job_functions.length &&
      !matchesAnySelectedValue(advanced.company.job_functions, jobFunctions)
    ) {
      return false;
    }

    if (
      advanced.activity_section.recruitment_activity.length &&
      !matchesAnySelectedValue(advanced.activity_section.recruitment_activity, recruitmentActivities)
    ) {
      return false;
    }
    if (
      advanced.activity_section.tags.length &&
      !matchesAnySelectedValue(advanced.activity_section.tags, tags)
    ) {
      return false;
    }
    if (
      advanced.activity_section.past_applications.length &&
      !matchesAnySelectedValue(advanced.activity_section.past_applications, pastApplications)
    ) {
      return false;
    }
    if (
      advanced.activity_section.notes.length &&
      !matchesAnySelectedValue(advanced.activity_section.notes, noteValues)
    ) {
      return false;
    }

    return true;
  });
};

const getStageValue = (row = null) => {
  const safe = row || {};
  return pickFirst(
    safe.stage,
    safe.candidate_stage,
    safe.candidateStage,
    safe.pipeline_stage,
    safe.pipelineStage,
    safe.current_stage,
    safe.currentStage,
    safe.stage_name,
    safe.stageName
  );
};

const getPipelineStageValue = (row = null) => {
  const assignmentStage = Array.isArray(row?.job_assignments)
    ? row.job_assignments
        .map((assignment) => assignment?.stage || assignment?.job_stage || "")
        .find((stage) => String(stage || "").trim())
    : "";
  return pickFirst(assignmentStage, row?.job_stage, row?.jobStage, getStageValue(row));
};

const isCandidateInPipeline = (row = null) =>
  Boolean(
    String(row?.job_id || row?.jobId || "").trim() ||
      String(row?.job_title || "").trim() ||
      (Array.isArray(row?.job_assignments) && row.job_assignments.length) ||
      (Array.isArray(row?.jobs_summary) && row.jobs_summary.length)
  );

const getStatusValue = (row = null) => {
  const safe = row || {};
  return pickFirst(
    safe.status,
    safe.candidate_status,
    safe.candidateStatus
  );
};

const getEmailValue = (row = null) => {
  const safe = row || {};
  return pickFirst(
    safe.email,
    safe.gmail,
    safe.gmail_address,
    safe.email_address,
    safe.mail,
    safe.candidate_email
  );
};

const getLinkedinValue = (row = null) => {
  const safe = row || {};
  return pickFirst(
    safe.linkedin_url,
    safe.linkedin,
    safe.linkedinUrl,
    safe.linkedin_profile,
    safe.linkedin_profile_url,
    safe.linkedinProfileUrl
  );
};

const escapeCsvValue = (value) => {
  const raw = String(value ?? "");
  if (!/[",\n]/.test(raw)) return raw;
  return `"${raw.replace(/"/g, '""')}"`;
};

const downloadCandidatesCsv = (rows = []) => {
  const headers = [
    "Candidate Name",
    "Current Title",
    "Current Company",
    "Associated Job",
    "Location",
    "Status",
    "Stage",
    "Owner",
    "Email",
    "Phone",
    "LinkedIn",
    "Skills",
    "Source",
  ];

  const lines = [
    headers.join(","),
    ...(rows || []).map((row) =>
      [
        row?.full_name || row?.candidate_name || row?.name || "",
        row?.candidate_title || row?.title || "",
        extractCurrentCompany(row),
        Array.isArray(row?.job_assignments)
          ? row.job_assignments.map((job) => job?.title).filter(Boolean).join("; ")
          : buildCandidateJobsSummary(row).join("; "),
        pickFirst(row?.candidate_location, row?.location, row?.current_location),
        getStatusValue(row),
        getStageValue(row),
        row?.owner || row?.recruiter_name || row?.assigned_team_member || "",
        getEmailValue(row),
        pickFirst(row?.phone, row?.phone_number),
        getLinkedinValue(row),
        parseSkillsFromAny(row).join(", "),
        row?.source || row?.last_sync_label || "",
      ]
        .map(escapeCsvValue)
        .join(",")
    ),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `candidates-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const truncateText = (value, max = 180) => {
  const text = cleanText(value);
  if (!text || text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 3)).trimEnd()}...`;
};

const buildCandidateAgentTaskPrompt = ({
  taskValue = "",
  candidates = [],
  scopeLabel = "filtered candidates",
}) => {
  const safeCandidates = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
  const limitedCandidates = safeCandidates.slice(0, AGENT_TASK_CANDIDATE_LIMIT);
  const detailCountLabel =
    limitedCandidates.length < safeCandidates.length
      ? `Detailed profiles below cover the first ${limitedCandidates.length} candidates.`
      : `Detailed profiles below cover all ${limitedCandidates.length} candidates.`;

  const profileLines = limitedCandidates.map((candidate, index) => {
    const name =
      cleanText(candidate?.full_name || candidate?.candidate_name || candidate?.name) ||
      `Candidate ${index + 1}`;
    const title = cleanText(candidate?.candidate_title || candidate?.title);
    const currentCompany = cleanText(extractCurrentCompany(candidate));
    const locations = extractCandidateLocations(candidate).slice(0, 3);
    const skills = parseSkillsFromAny(candidate).slice(0, 8);
    const jobFunctions = extractCandidateJobFunctions(candidate).slice(0, 4);
    const pastCompanies = extractPastCompanies(candidate).slice(0, 3);
    const languages = extractCandidateLanguages(candidate).slice(0, 4);
    const status = cleanText(getStatusValue(candidate));
    const stage = cleanText(getStageValue(candidate));
    const email = cleanText(getEmailValue(candidate));
    const linkedin = cleanText(getLinkedinValue(candidate));
    const notes = truncateText(candidate?.notes, 200);
    const yearsOfExperience = estimateYearsOfExperience(candidate);
    const yearsLabel =
      Number.isFinite(yearsOfExperience) && yearsOfExperience > 0
        ? `${Math.round(yearsOfExperience * 10) / 10} years`
        : "";

    return [
      `${index + 1}. ${name}`,
      title ? `Title: ${title}` : "",
      currentCompany ? `Current company: ${currentCompany}` : "",
      locations.length ? `Location: ${locations.join(", ")}` : "",
      yearsLabel ? `Estimated experience: ${yearsLabel}` : "",
      jobFunctions.length ? `Target roles: ${jobFunctions.join(", ")}` : "",
      skills.length ? `Skills: ${skills.join(", ")}` : "",
      pastCompanies.length ? `Past companies: ${pastCompanies.join(", ")}` : "",
      languages.length ? `Languages: ${languages.join(", ")}` : "",
      status || stage
        ? `Pipeline: ${[status, stage].filter(Boolean).join(" / ")}`
        : "",
      email ? `Email: ${email}` : "",
      linkedin ? `LinkedIn: ${linkedin}` : "",
      notes ? `Notes: ${notes}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  });

  const taskInstructions = {
    "enrich-profile": [
      "Enrich each candidate profile using only public, verifiable information.",
      "Identify missing or outdated profile details, likely skills, career progression, and relevant public sources.",
      "Do not invent contact details, employment history, or credentials. Clearly distinguish verified facts from suggestions.",
    ],
    "build-pitch-package": [
      "Build a concise candidate pitch package for each candidate.",
      "Include a recruiter-ready professional summary, standout strengths, relevant achievements, target roles, and any risks or gaps to address.",
      "Write the package so it can be tailored quickly for a client submission without inventing facts.",
    ],
    "build-sales-sequence": [
      "Build a personalized outreach sales sequence for each candidate.",
      "Provide a short multi-step sequence with subject lines, email copy, timing, and light calls to action tied to the candidate's likely career goals.",
      "Keep it credible and do not make claims that are not supported by the candidate profile.",
    ],
    "find-job-leads": [
      "Find public job leads that are likely to fit each candidate.",
      "Return role title, company, location, job URL, source URL, fit rationale, and any obvious requirements or gaps.",
      "Only include leads supported by public sources; do not fabricate openings or links.",
    ],
  };

  const instructions = taskInstructions[taskValue] || [
    `Create a new recruiting AI agent task for this ${scopeLabel}.`,
    "Review the group, identify the strongest profiles, cluster obvious patterns, and recommend next actions.",
  ];

  return [
    ...instructions,
    "",
    `Candidate count: ${safeCandidates.length}.`,
    detailCountLabel,
    "",
    "Candidate profiles:",
    profileLines.join("\n\n"),
  ]
    .filter(Boolean)
    .join("\n");
};

const getResumeValue = (row = null) => {
  const safe = row || {};
  return pickFirst(
    safe.resume_url,
    safe.resume,
    safe.resumeUrl,
    safe.cv_url,
    safe.cvUrl,
    safe.document_url,
    safe.documentUrl
  );
};

const parseSkillsFromAny = (row = null) => {
  const safe = row || {};

  const fromJson = [
    ...safeJsonArray(safe.skills_json),
    ...safeJsonArray(safe.candidate_skills_json),
    ...safeJsonArray(safe.skillsJson),
    ...safeJsonArray(safe.skill_list_json),
    ...safeJsonArray(safe.skillListJson),
  ];

  const fromStrings = [
    ...asArray(safe.skills),
    ...asArray(safe.skill),
    ...asArray(safe.skill_list),
    ...asArray(safe.skillList),
    ...asArray(safe.skillset),
    ...asArray(safe.skills_text),
    ...asArray(safe.skills_csv),
    ...asArray(safe.skill_csv),
  ];

  const fromExperience = safeJsonArray(safe.experience_items).flatMap((item) => {
    if (Array.isArray(item?.skills)) return item.skills;
    if (Array.isArray(item?.technologies)) return item.technologies;
    if (typeof item?.skills === "string") return asArray(item.skills);
    if (typeof item?.technologies === "string") return asArray(item.technologies);
    return [];
  });

  return uniqueSorted([...fromJson, ...fromStrings, ...fromExperience]);
};

const statusStyles = {
  active: { color: "#12B76A", bg: "#ECFDF3", border: "#ABEFC6" },
  "not available": { color: "#F04438", bg: "#FEF3F2", border: "#FECDCA" },
  "do not contact": { color: "#667085", bg: "#F2F4F7", border: "#D0D5DD" },
  default: { color: "#475467", bg: "#F8FAFC", border: "#E2E8F0" },
};

const BULK_STATUS_OPTIONS = CANDIDATE_STATUS_VALUES;
const BULK_STAGE_OPTIONS = RECRUITING_STAGES.map(stage => stage.label);

function StatusChip({ value }) {
  const key = normalizeStatusKey(value);
  const palette = recruitingStageColors(key);
  const style = palette
    ? { color: palette.color, bg: palette.activeBackground, border: palette.color }
    : statusStyles[key] || statusStyles.default;

  return (
    <Chip
      label={value || EMPTY_VALUE}
      size="small"
      sx={{
        height: 24,
        fontWeight: 600,
        fontSize: 11,
        borderRadius: "999px",
        color: style.color,
        backgroundColor: style.bg,
        border: `1px solid ${style.border}`,
        "& .MuiChip-label": { px: 1.2 },
      }}
    />
  );
}

function StageText({ value }) {

  return (
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 500,
        color: stageColor(value),
        whiteSpace: "nowrap",
      }}
    >
      {value || EMPTY_VALUE}
    </Typography>
  );
}

function JobsCell({ row, onOpenJob }) {
  const assignments = Array.isArray(row?.job_assignments)
    ? row.job_assignments
        .map((job) => ({
          id: job?.id ?? job?.job_id,
          title: cleanText(job?.title || job?.job_title || job?.name),
          stage: cleanText(job?.stage || job?.job_stage),
        }))
        .filter((job) => job.title)
    : [];
  const jobs = assignments.length
    ? assignments.map((job) => job.title)
    : Array.isArray(row.jobs_summary)
      ? row.jobs_summary
      : buildCandidateJobsSummary(row);
  const firstJob = jobs[0] || "";
  const firstJobId = assignments[0]?.id ?? row?.job_id ?? row?.jobId ?? null;
  const firstStage = assignments[0]?.stage || getPipelineStageValue(row);
  const extra = Math.max(0, jobs.length - 1);

  if (!firstJob) {
    return <Typography sx={{ fontSize: 14, color: MUTED }}>{EMPTY_VALUE}</Typography>;
  }

  return (
    <Stack spacing={0.15} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap">
        <Typography
          component={firstJobId ? "button" : "span"}
          type={firstJobId ? "button" : undefined}
          onClick={
            firstJobId
              ? (event) => {
                  event.stopPropagation();
                  onOpenJob?.(firstJobId);
                }
              : undefined
          }
          sx={{
            fontSize: 14,
            color: firstJobId ? PURPLE : "#667085",
            background: "transparent",
            border: 0,
            p: 0,
            cursor: firstJobId ? "pointer" : "default",
            textAlign: "left",
            whiteSpace: "nowrap",
            maxWidth: 180,
            "&:hover": firstJobId ? { textDecoration: "underline" } : undefined,
          }}
          noWrap
        >
          {firstJob}
        </Typography>

        {extra > 0 && (
          <Chip
            label={`+${extra}`}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              borderRadius: "999px",
              color: PURPLE,
              backgroundColor: "#F4EBFF",
              border: "1px solid #E9D7FE",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        )}
      </Stack>
      {firstStage ? (
        <Typography sx={{ color: "#98A2B3", fontSize: 12 }} noWrap>
          Stage: {firstStage}
        </Typography>
      ) : null}
    </Stack>
  );
}

function resolveCandidateResumeUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;

  const publicBase = normalizeApiBase(API_BASE).replace(/\/api\/?$/, "");
  const path = raw.startsWith("/")
    ? raw
    : raw.startsWith("candidate_resumes/")
    ? `/uploads/${raw}`
    : `/${raw}`;

  return publicBase ? `${publicBase}${path}` : path;
}

function ContactInfoCell({ row, onOpenResume }) {
  const emailValue = getEmailValue(row);
  const phoneValue = pickFirst(row?.phone, row?.phone_number);
  const linkedinValue = getLinkedinValue(row);
  const resumeUrl = resolveCandidateResumeUrl(getResumeValue(row));
  const candidateName = row?.full_name || row?.candidate_name || "Candidate";

  return (
    <InfoActionIcons
      actions={[
        { type: "email", href: emailValue, title: "Email" },
        { type: "phone", href: phoneValue, title: "Phone" },
        { type: "linkedin", href: linkedinValue, title: "LinkedIn" },
        {
          type: "resume",
          href: resumeUrl,
          title: "Open CV",
          onClick: resumeUrl
            ? (event) => {
                event.preventDefault();
                onOpenResume?.({ name: candidateName, url: resumeUrl });
              }
            : undefined,
        },
      ]}
      showDisabled={true}
      showGroupDivider={false}
      preserveOrder={true}
      gap={0.85}
      iconSize={18}
    />
  );
}

function CandidateResumePreviewDialog({ resume, onClose }) {
  const resumeUrl = String(resume?.url || "").trim();

  return (
    <Dialog
      open={Boolean(resumeUrl)}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", md: "min(1000px, calc(100% - 80px))" },
          height: { xs: "calc(100% - 24px)", md: "calc(100% - 64px)" },
          maxWidth: "1000px",
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}
      >
        <Typography sx={{ color: TEXT, fontSize: 14, fontWeight: 700 }} noWrap>
          {resume?.name || "Candidate CV"}
        </Typography>
        <Stack data-popup-header direction="row" spacing={0.5} alignItems="center">
          <IconButton
            component="a"
            href={resumeUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open CV in new tab"
            size="small"
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton data-popup-close onClick={onClose} aria-label="Close CV preview" size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Stack>
      {resumeUrl ? (
        <Box sx={{ flex: 1, minHeight: 0, backgroundColor: "#F8FAFC" }}>
          <iframe
            title={`${resume?.name || "Candidate"} CV`}
            src={`${resumeUrl}#toolbar=0&navpanes=0&view=FitH`}
            style={{ width: "100%", height: "100%", minHeight: "620px", border: 0, display: "block" }}
          />
        </Box>
      ) : null}
    </Dialog>
  );
}

function LastActivityCell({ row }) {
  const activityAt = extractCandidateLastActivityAt(row);
  const primaryValue = formatRelativeTimeShort(activityAt, { emptyValue: EMPTY_VALUE });

  return (
    <Typography sx={{ fontSize: 14, color: TEXT }} noWrap>
      {primaryValue}
    </Typography>
  );
}

function FieldRow({ label, value, link }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "78px 1fr",
        gap: 1,
        py: 0.65,
        alignItems: "start",
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: BLUE }}>
        {label}
      </Typography>

      {link ? (
        <Typography
          component="a"
          href={link}
          target="_blank"
          rel="noreferrer"
          sx={{
            fontSize: 11,
            fontWeight: 400,
            color: PURPLE,
            textDecoration: "none",
            wordBreak: "break-word",
          }}
        >
          {value || EMPTY_VALUE}
        </Typography>
      ) : (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            color: "#475467",
            wordBreak: "break-word",
          }}
        >
          {value || EMPTY_VALUE}
        </Typography>
      )}
    </Box>
  );
}

function CandidateDrawerSocialIcons({ linkedin, email, resume }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <InfoActionIcons
        showDisabled={true}
        gap={1.25}
        iconSize={16}
        variant="filled"
        filledWidth={48}
        filledHeight={34}
        showGroupDivider={false}
        actions={[
          { type: "linkedin", href: linkedin },
          { type: "email", href: email },
          { type: "resume", href: resume },
        ]}
      />
    </Box>
  );
}

function CandidateProfileDrawer({ open, onClose, candidate, loading, onShowMore }) {
  const safeCandidate = candidate || {};
  const [hiringManagers, setHiringManagers] = useState([]);
  const [hiringManagersLoading, setHiringManagersLoading] = useState(false);
  const candidateId = safeCandidate.candidate_id || safeCandidate.id || null;

  useEffect(() => {
    if (!open || !candidateId) {
      if (!open) {
        setHiringManagers([]);
        setHiringManagersLoading(false);
      }
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setHiringManagers([]);
    setHiringManagersLoading(true);

    fetchHiringManagers({
      apiBase: API_BASE,
      entityType: "candidate",
      entityId: candidateId,
      signal: controller.signal,
    })
      .then((rows) => {
        if (!active) return;
        setHiringManagers((Array.isArray(rows) ? rows : []).filter((row) => isHiringManagerContactRow(row)));
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to fetch candidate hiring managers:", error);
      })
      .finally(() => {
        if (active) setHiringManagersLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [candidateId, open]);

  const name =
    safeCandidate.full_name ||
    safeCandidate.candidate_name ||
    safeCandidate.name ||
    "Candidate";
  const profileHeading =
    name && name !== "Candidate" ? `Candidate Profile: ${name}` : "Candidate Profile";

  const email = getEmailValue(safeCandidate);
  const title = safeCandidate.title || safeCandidate.candidate_title || EMPTY_VALUE;
  const phone = safeCandidate.phone || safeCandidate.phone_number || EMPTY_VALUE;
  const linkedin = getLinkedinValue(safeCandidate);
  const resume = getResumeValue(safeCandidate);
  const source = safeCandidate.source || safeCandidate.last_sync_label || "LinkedIn";
  const owner =
    safeCandidate.recruiter_name ||
    safeCandidate.owner ||
    safeCandidate.assigned_team_member ||
    EMPTY_VALUE;
  const createdAt = safeCandidate.created_at || safeCandidate.updated_at || "";
  const lastActivityAt = extractCandidateLastActivityAt(safeCandidate);
  const lastActivityLabel = formatRelativeTimeShort(lastActivityAt, { emptyValue: EMPTY_VALUE });
  const notes = safeCandidate.notes || "";
  const preferences = safeJsonObject(safeCandidate.preferences_json);
  const prefWhyLooking = safeCandidate.pref_why_looking || preferences?.whyLooking || "";
  const prefTimeline = safeCandidate.pref_timeline || preferences?.timeline || "";
  const lastActivitySummary =
    safeCandidate.last_activity_detail ||
    safeCandidate.last_activity_message ||
    notes ||
    prefWhyLooking ||
    `${email || "Candidate"} profile created`;

  const stages = [
    { label: "Applied", count: 5 },
    { label: "Matched", count: 2 },
    { label: "Sourced", count: 3 },
    { label: "Contacted", count: 4 },
    { label: "Screened", count: 4 },
    { label: "Interview", count: 4 },
    { label: "Assessment", count: 2 },
    { label: "Offer", count: 2 },
    { label: "Rejected", count: 2 },
  ];

  const skills = Array.isArray(safeCandidate.skills)
    ? safeCandidate.skills
    : parseSkillsFromAny(safeCandidate);
  const fallbackHiringManagers = dedupeHiringManagerRows(
    safeCandidate.hiring_manager_name || safeCandidate.hiring_manager
      ? [
          {
            name: safeCandidate.hiring_manager_name || safeCandidate.hiring_manager,
            role: "Hiring Manager",
          },
        ]
      : []
  );
  const hiringManagersPreview = (hiringManagers.length ? hiringManagers : fallbackHiringManagers).slice(0, 3);

  const initials = (() => {
    const parts = String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
  })();

  const headerLeft = (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "999px",
          border: "1.5px solid #6366f1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <User size="11" color="#6366f1" variant="Outline" />
      </Box>

      <Typography sx={{ fontSize: 12, fontWeight: 700, color: BLUE }}>
        {profileHeading}
      </Typography>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="candidate-profile-drawer"
      width={430}
      headerLeft={headerLeft}
      closeVariant="text"
      closeColor="#9C4FFF"
      paperSx={{
        borderRadius: "16px 0 0 16px",
        boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)",
      }}
      footer={
        <Button
          fullWidth
          variant="contained"
          size="small"
          disabled={!candidate}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            height: 28,
            minHeight: 28,
            fontSize: 10,
            fontWeight: 600,
            background: "linear-gradient(135deg,#7C3AED,#9C4FFF)",
            boxShadow: "none",
            "&:hover": {
              background: "linear-gradient(135deg,#7C3AED,#9C4FFF)",
              boxShadow: "none",
            },
          }}
          onClick={() => {
            onClose?.();
            onShowMore?.("details");
          }}
        >
          Show more
        </Button>
      }
    >
      {loading ? (
        <Box sx={{ py: 3 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <CircularProgress size={18} />
            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
              Loading candidate profile...
            </Typography>
          </Stack>
        </Box>
      ) : !candidate ? (
        <Alert severity="info">No candidate selected.</Alert>
      ) : (
        <Box>
          {open && <CandidateRecruitingActions candidate={candidate} />}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.75 }}>
            <Avatar
              src={safeCandidate.avatar_url || ""}
              alt={name}
              sx={{
                width: 38,
                height: 38,
                fontSize: 12,
                fontWeight: 700,
                background: "radial-gradient(circle at 30% 30%, #a855f7, #6366f1)",
              }}
            >
              {(initials || "?").toUpperCase()}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10.5, color: MUTED }} noWrap>
                {title}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.75 }}>
            {skills.slice(0, 3).map((skill, idx) => (
              <Chip
                key={`${skill}-${idx}`}
                label={skill}
                size="small"
                sx={{
                  height: 20,
                  borderRadius: "999px",
                  fontSize: 9.5,
                  fontWeight: 500,
                  color: BLUE,
                  backgroundColor: "#F4F3FF",
                  border: "1px solid #E9D7FE",
                }}
              />
            ))}

            {skills.length > 3 ? (
              <Chip
                label={`+${skills.length - 3}`}
                size="small"
                sx={{
                  height: 20,
                  borderRadius: "999px",
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: "#667085",
                  backgroundColor: "#F2F4F7",
                  border: "1px solid #EAECF0",
                }}
              />
            ) : null}
          </Box>

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.9 }}>
            Quick Actions:
          </Typography>

          <CandidateDrawerSocialIcons linkedin={linkedin} email={email} resume={resume} />

          <Divider sx={{ mb: 1.5 }} />

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 1 }}>
            Stages:
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            {stages.map((item) => (
              <Typography key={item.label} sx={{ fontSize: 10, color: PURPLE, fontWeight: 500 }}>
                {item.label} ({item.count})
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
            <Typography
              sx={{ fontSize: 10.5, color: PURPLE, fontWeight: 600, cursor: "pointer" }}
              onClick={() => {
                onClose?.();
                onShowMore?.("stages");
              }}
            >
              View all
            </Typography>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 1 }}>
            Last Activity:
          </Typography>

          <Typography sx={{ fontSize: 10.5, color: "#98A2B3", mb: 0.75 }}>
            {lastActivityLabel}
          </Typography>

          <Typography sx={{ fontSize: 10.5, color: "#475467", lineHeight: 1.45 }}>
            {lastActivitySummary}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.6, mb: 1.5 }}>
            <Typography
              sx={{ fontSize: 10.5, color: PURPLE, fontWeight: 600, cursor: "pointer" }}
              onClick={() => {
                onClose?.();
                onShowMore?.("activities");
              }}
            >
              View
            </Typography>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 1 }}>
            Contacts
          </Typography>

          <FieldRow label="Phone" value={phone} />
          <FieldRow label="Email" value={email} link={email ? `mailto:${email}` : undefined} />
          <FieldRow label="LinkedIn" value={linkedin || EMPTY_VALUE} link={linkedin || undefined} />
          <FieldRow label="Resume" value={resume || EMPTY_VALUE} link={resume || undefined} />

          <Divider sx={{ my: 1.5 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.7 }}>
                Hiring Managers
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: 10, color: "#475467" }}>
                Recruiter:{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {owner || EMPTY_VALUE}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 9.5, color: "#98A2B3" }}>
                Created: {createdAt ? String(createdAt) : EMPTY_VALUE}
              </Typography>
            </Box>
          </Box>

          {hiringManagersLoading ? (
            <Typography sx={{ fontSize: 10.5, color: MUTED, mb: 1.25 }}>
              Loading hiring managers...
            </Typography>
          ) : hiringManagersPreview.length ? (
            <Box sx={{ mb: 1.25 }}>
              {hiringManagersPreview.map((manager, index) => (
                <Box
                  key={`${manager.id ?? manager.name}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                    alignItems: "center",
                    py: 0.55,
                    borderBottom:
                      index === hiringManagersPreview.length - 1
                        ? "none"
                        : `1px solid ${BORDER}`,
                  }}
                >
                  <Typography sx={{ fontSize: 10.5, color: TEXT }} noWrap>
                    {manager.name}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: MUTED }} noWrap>
                    {manager.role}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 10.5, color: MUTED, mb: 1.25 }}>
              No hiring managers added yet.
            </Typography>
          )}

          <Box>
            <Typography sx={{ fontSize: 10, color: "#98A2B3", mb: 0.45 }}>
              Source
            </Typography>
            <Box
              sx={{
                height: 34,
                border: `1px solid #D0D5DD`,
                borderRadius: "6px",
                px: 1.25,
                display: "flex",
                alignItems: "center",
                fontSize: 11,
                color: "#475467",
              }}
            >
              {source || "LinkedIn"}
            </Box>
          </Box>

          {prefTimeline ? (
            <Typography sx={{ fontSize: 10.5, color: "#667085", mt: 1.25 }}>
              Timeline: {prefTimeline}
            </Typography>
          ) : null}
        </Box>
      )}
    </ReusableRightDrawer>
  );
}

function FilterSection({ title, children }) {
  return (
    <Box sx={{ pb: 1.4, mb: 1.35, borderBottom: `1px solid ${BORDER}` }}>
      <Typography data-popup-section sx={{ fontSize: 14, fontWeight: 700, color: BLUE, mb: 0.9, fontFamily: "Be Vietnam Pro" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function CandidateFilterDrawer({
  open,
  onClose,
  endpoint = "/api/candidates",
  draft,
  setDraft,
  onApply,
  onClear,
  onAdvancedSearch,
}) {
  const directory = usePageFilterOptions(open, endpoint, draft, setDraft);
  const options = directory.options;
  const inputSx = popupFilterInputSx(PURPLE);
  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const headerLeft = (
    <Box display="flex" alignItems="center" gap={1}>
      <Filter size="14" color={BLUE} variant="Linear" />
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: BLUE, fontFamily: "Be Vietnam Pro" }}>
        Filter by
      </Typography>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="candidate-filter-drawer"
      width={430}
      headerLeft={headerLeft}
      closeVariant="text"
      closeColor="#9C4FFF"
      paperSx={{
        borderRadius: "16px 0 0 16px",
        boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)",
      }}
      footer={
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={onAdvancedSearch}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 800,
              px: 2.2,
              fontFamily: "Be Vietnam Pro",
              background: "linear-gradient(135deg,#7C3AED,#9C4FFF)",
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(135deg,#7C3AED,#9C4FFF)",
                boxShadow: "none",
              },
            }}
          >
            Advance Search
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onClear}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 800,
                px: 1.8,
                fontFamily: "Be Vietnam Pro",
                color: PURPLE,
                borderColor: PURPLE,
              }}
            >
              Clear Filters
            </Button>

            <Button
              size="small"
              variant="contained"
              onClick={onApply}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 800,
                px: 1.8,
                fontFamily: "Be Vietnam Pro",
                background: "linear-gradient(135deg,#7C3AED,#9C4FFF)",
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(135deg,#7C3AED,#9C4FFF)",
                  boxShadow: "none",
                },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      }
    >
      {directory.loading && <Typography role="status">Loading all filter values...</Typography>}
      {directory.error && <Alert severity="error">{directory.error}</Alert>}
      <FilterSection title="AI Agent:">
        <TextField
          fullWidth
          size="small"
          placeholder="Type our request"
          value={draft.aiRequest}
          onChange={(e) => setField("aiRequest", e.target.value)}
          sx={inputSx}
        />
      </FilterSection>

      <FilterSection title="Candidate Name:">
        <PopupMultiSelectField
          freeSolo={false}
          options={options.names}
          {...directory.fieldProps("names")}
          value={draft.names}
          onChange={(next) => setField("names", uniqueSorted(next))}
          placeholder="Type Name"
          inputSx={inputSx}
        />
      </FilterSection>

      <FilterSection title="Skills:">
        <PopupMultiSelectField
          freeSolo={false}
          options={options.skills}
          {...directory.fieldProps("skills")}
          value={draft.skills}
          onChange={(next) => setField("skills", uniqueSorted(next))}
          placeholder="Skill keyword anywhere on profile"
          inputSx={inputSx}
        />
      </FilterSection>

      <FilterSection title="Locations:">
        <LocationMultiSelectField
          freeSolo={false}
          searchExternal={false}
          options={options.locations}
          {...directory.fieldProps("locations")}
          value={draft.locations}
          onChange={(next) => setField("locations", normalizeLocationList(next))}
          placeholder="Select location"
          inputSx={inputSx}
          autocompleteProps={{ ...directory.fieldProps("locations").autocompleteProps, groupBy: groupLocationOption }}
        />
      </FilterSection>

      <FilterSection title="Team Member:">
        <PopupMultiSelectField
          freeSolo={false}
          options={options.owners}
          {...directory.fieldProps("owners")}
          value={draft.owners}
          onChange={(next) => setField("owners", uniqueSorted(next))}
          placeholder="Select one or more team members"
          inputSx={inputSx}
        />
      </FilterSection>

      <FilterSection title="Status:">
        <PopupMultiSelectField
          freeSolo={false}
          options={options.statuses}
          {...directory.fieldProps("statuses")}
          value={draft.statuses}
          onChange={(next) => setField("statuses", uniqueSorted(next))}
          placeholder="Select one or more Status"
          inputSx={inputSx}
        />
      </FilterSection>

      <FilterSection title="Stage:">
        <PopupMultiSelectField
          freeSolo={false}
          options={options.stages}
          {...directory.fieldProps("stages")}
          value={draft.stages}
          onChange={(next) => setField("stages", uniqueSorted(next))}
          placeholder="Select one or more Stages"
          inputSx={inputSx}
        />
      </FilterSection>

      <Box sx={{ pb: 0.5 }}>
        <Typography data-popup-section sx={{ fontSize: 14, fontWeight: 700, color: BLUE, mb: 0.9, fontFamily: "Be Vietnam Pro" }}>
          Source:
        </Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.sources}
          {...directory.fieldProps("sources")}
          value={draft.sources}
          onChange={(next) => setField("sources", uniqueSorted(next))}
          placeholder="Select one or more Sources"
          inputSx={inputSx}
        />
      </Box>
    </ReusableRightDrawer>
  );
}

const createEmptyFilterDraft = () => ({
  aiRequest: "",
  names: [],
  skills: [],
  locations: [],
  owners: [],
  statuses: [],
  stages: [],
  sources: [],
});

function CandidatesContent({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  useParams();

  const [candidates, setCandidates] = useState([]);
  const [candidateTotal, setCandidateTotal] = useState(0);
  const [candidateListTotal, setCandidateListTotal] = useState(null);
  const [recruitingPipelineTotal, setRecruitingPipelineTotal] = useState(null);
  const [candidateStats, setCandidateStats] = useState(null);
  const candidateRequestId = useRef(0);
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidateRowsPerPage, setCandidateRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);
  const [resumePreview, setResumePreview] = useState(null);
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useCsvImport();
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);
  const [candidatePitchPackageDialog, setCandidatePitchPackageDialog] = useState({
    open: false,
    candidates: [],
  });
  const [launchAgentMenuAnchorEl, setLaunchAgentMenuAnchorEl] = useState(null);
  const [candidateEnrichmentStatusById, setCandidateEnrichmentStatusById] = useState({});
  const [activeEnrichmentCandidateIds, setActiveEnrichmentCandidateIds] = useState([]);
  const [agentDialogInitialPrompt, setAgentDialogInitialPrompt] = useState("");
  const [agentDialogSubmitOnOpen, setAgentDialogSubmitOnOpen] = useState(false);
  const [sequenceDialogItemsOverride, setSequenceDialogItemsOverride] = useState(null);
  const [sequenceDialogInitialCampaignType, setSequenceDialogInitialCampaignType] = useState("recruiting");
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [candidateDrawerOpen, setCandidateDrawerOpen] = useState(false);
  const [candidateDrawerLoading, setCandidateDrawerLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState(createEmptyFilterDraft());
  const [appliedFilters, setAppliedFilters] = useState(createEmptyFilterDraft());
  const [activeStatId, setActiveStatId] = useState(null);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedApplied, setAdvancedApplied] = useState(null);
  const hasCandidateClientFilters = useMemo(
    () =>
      Boolean(search.trim()) ||
      Boolean(advancedApplied) ||
      Object.values(appliedFilters).some((value) =>
        Array.isArray(value) ? value.length > 0 : Boolean(String(value || "").trim())
      ),
    [advancedApplied, appliedFilters, search]
  );
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [bulkActionBusy, setBulkActionBusy] = useState(false);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuCandidate, setRowMenuCandidate] = useState(null);
  const [actionToast, setActionToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [viewMode, setViewMode] = useState("list");
  const [defaultTab, setDefaultTab] = useState("details");
  const [listTab, setListTab] = useState("list");
  const sidebarResetToken = getSidebarResetToken(location, "/candidates");
  const requestedCandidateProfileId = sidebarResetToken ? "" :
    new URLSearchParams(location.search).get("candidateId") || location.state?.createdCandidateId || "";

  useEffect(() => {
    if (!sidebarResetToken) return;

    setShowAdvancedSearch(false);
    setCandidateDrawerOpen(false);
    setCandidateDrawerLoading(false);
    setBulkActionBusy(false);
    setIsAddCandidateOpen(false);
    setIsAgentDialogOpen(false);
    setCandidatePitchPackageDialog({ open: false, candidates: [] });
    setAgentDialogInitialPrompt("");
    setAgentDialogSubmitOnOpen(false);
    setIsAddToSequenceOpen(false);
    setSequenceDialogItemsOverride(null);
    setSequenceDialogInitialCampaignType("recruiting");
    setRowMenuAnchorEl(null);
    setRowMenuCandidate(null);
    setSelectedCandidate(null);
    setViewMode("list");
    setDefaultTab("details");
    setActiveStatId(null);
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate, sidebarResetToken]);

  const overviewMetric = useMemo(
    () => new URLSearchParams(location.search).get("overviewMetric") || "",
    [location.search]
  );
  const pipelineStageFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("stage") || "",
    [location.search]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedView = params.get("view");
    setListTab(
      requestedView === "pipeline" || requestedView === "recruiting-pipeline"
        ? "pipeline"
        : "list"
    );
  }, [location.search]);

  useEffect(() => {
    const stageByMetric = {
      candidate_interview: "Interview",
      candidate_offer: "Offer",
      hired_this_month: "Hired",
    };
    const stage = stageByMetric[overviewMetric] || pipelineStageFromUrl;
    if (!stage) return;

    const nextFilters = createEmptyFilterDraft();
    setFilterDraft(nextFilters);
    setAppliedFilters(nextFilters);
    setActiveStatId(pipelineStageKey(stage));
    setListTab("pipeline");
    setCandidatePage(1);
  }, [overviewMetric, pipelineStageFromUrl]);

  const fetchCandidatesOnly = useCallback(async ({ showLoading = true } = {}) => {
      const requestId = ++candidateRequestId.current;
      try {
        if (showLoading) setLoading(true);
        setLoadError("");

        const loadCandidateRows = async () => {
          const base = normalizeApiBase(API_BASE);
          const query = new URLSearchParams({
            page: String(hasCandidateClientFilters ? 1 : candidatePage),
            // Basic and advanced candidate filters are evaluated in the UI.
            // Load their complete result set so the table can paginate it,
            // rather than filtering just the current server page.
            limit: String(hasCandidateClientFilters ? 1000 : candidateRowsPerPage),
          });
          if (listTab === "pipeline") query.set("has_job", "1");
          else query.set("funnel", "status");
          if (activeStatId) query.set("stats_filter", activeStatId);
          const candidatesEndpoint = `${base}/api/candidates?${query.toString()}`;
          const candidatesRes = await fetch(candidatesEndpoint, {
            method: "GET",
            credentials: "include",
            headers: authHeaders(),
          });

          if (!candidatesRes.ok) {
            const text = await candidatesRes.text().catch(() => "");
            throw new Error(`Failed to fetch candidates (${candidatesRes.status}). ${text.slice(0, 200)}`);
          }

          const candidatesData = await candidatesRes.json().catch(() => ({}));
          const candidateRows = extractRows(candidatesData);
          const reportedTotal = Number(candidatesData?.total);
          if (hasCandidateClientFilters) {
            // Client filters must see the entire stage, including uploads larger than the API page limit.
            for (let nextPage = 2; candidateRows.length < reportedTotal; nextPage += 1) {
              query.set("page", String(nextPage));
              const response = await fetch(`${base}/api/candidates?${query}`, {
                credentials: "include", headers: authHeaders(),
              });
              if (!response.ok) throw new Error("Failed to load all pipeline candidates.");
              const batch = extractRows(await response.json());
              if (!batch.length) throw new Error("The pipeline changed while loading. Please try again.");
              candidateRows.push(...batch);
            }
          }

          const mappedRows = candidateRows.map((row, idx) => {
            const preferences = safeJsonObject(row?.preferences_json || row?.preferences);
            const preferencesJson = stringifyCandidatePreferences(row?.preferences_json || row?.preferences || preferences);
            const sourceRow = {
              ...row,
              preferences_json: preferencesJson,
            };

            const parsedSkills = parseSkillsFromAny(sourceRow);
            const stageValue = getStageValue(sourceRow);
            const statusValue = getStatusValue(sourceRow) || "New";
            const emailValue = getEmailValue(sourceRow);
            const linkedinValue = getLinkedinValue(sourceRow);
            const resumeValue = getResumeValue(sourceRow);
            const explicitTags = extractCandidateExplicitTags(sourceRow);
            const jobsSummary =
              Array.isArray(row?.jobs_summary) && row.jobs_summary.length
                ? row.jobs_summary
                : buildCandidateJobsSummary(sourceRow);
            const lastActivityAt = extractCandidateLastActivityAt(sourceRow);
            const lastActivitySummary = extractCandidateLastActivitySummary(sourceRow);

            const companyName = pickFirst(
              row?.company,
              row?.candidate_company,
              row?.company_name,
              row?.current_company,
              row?.currentCompany,
              row?.company_client,
              row?.client_name,
              preferences?.current_company,
              preferences?.currentCompany,
              preferences?.company_client,
              extractCurrentCompany(row)
            );

            return {
              ...row,
              relation_id: row?.id ?? idx + 1,
              id: row?.id ?? idx + 1,
              candidate_id: row?.candidate_id ?? row?.id ?? idx + 1,

              full_name: pickFirst(row?.full_name, row?.candidate_name, row?.name),
              candidate_name: pickFirst(row?.full_name, row?.candidate_name, row?.name),

              title: pickFirst(row?.title, row?.candidate_title, row?.job_title, row?.position),
              candidate_title: pickFirst(row?.title, row?.candidate_title, row?.job_title, row?.position),

              avatar_url: pickFirst(row?.avatar_url, row?.profile_image, row?.photo_url),

              email: emailValue,
              gmail: emailValue,

              phone: pickFirst(row?.phone, row?.phone_number, row?.mobile),
              phone_number: pickFirst(row?.phone_number, row?.phone, row?.mobile),

              status: statusValue,
              candidate_status: statusValue,

              company: companyName,
              candidate_company: companyName,
              company_client: pickFirst(row?.company_client, preferences?.company_client, companyName),
              current_company: pickFirst(
                row?.current_company,
                row?.currentCompany,
                preferences?.current_company,
                preferences?.currentCompany,
                companyName
              ),
              currentCompany: pickFirst(
                row?.currentCompany,
                row?.current_company,
                preferences?.currentCompany,
                preferences?.current_company,
                companyName
              ),
              company_industry: pickFirst(
                row?.company_industry,
                row?.industry,
                row?.industry_sector
              ),
              company_size: pickFirst(
                row?.company_size,
                row?.size_range
              ),
              company_type: pickFirst(
                row?.company_type,
                row?.ownership_type
              ),

              location: pickFirst(row?.location, row?.candidate_location, row?.city_state, row?.current_location),
              candidate_location: pickFirst(row?.location, row?.candidate_location, row?.city_state, row?.current_location),

              stage: stageValue,
              candidate_stage: stageValue,

              recruiter_name: pickFirst(
                row?.owner,
                row?.recruiter_name,
                row?.recruiter,
                row?.owner_name,
                row?.assigned_team_member
              ),
              owner: pickFirst(
                row?.owner,
                row?.recruiter_name,
                row?.recruiter,
                row?.owner_name,
                row?.assigned_team_member
              ),

              linkedin_url: linkedinValue,
              resume_url: resumeValue,

              source: pickFirst(row?.source, row?.last_sync_label, row?.origin),
              notes: pickFirst(row?.notes, preferences?.notes),
              tags: explicitTags,
              candidate_tags: explicitTags,
              jobs_summary: jobsSummary,
              last_activity_at: lastActivityAt || null,
              last_activity_time: pickFirst(row?.last_activity_time, lastActivityAt) || null,
              last_activity_detail: pickFirst(row?.last_activity_detail, lastActivitySummary),
              last_activity_message: pickFirst(row?.last_activity_message, lastActivitySummary),

              skills: parsedSkills,
              skills_json:
                typeof row?.skills_json === "string" && row.skills_json.trim()
                  ? row.skills_json
                  : JSON.stringify(parsedSkills),

              extra_skills_count:
                Number(row?.extra_skills_count) || Math.max(0, parsedSkills.length - 1),

              matching_score: Number(
                row?.matching_score ??
                  row?.best_match_percentage ??
                  row?.bestMatch ??
                  0
              ),

              has_linkedin: Number(row?.has_linkedin) === 1 || Boolean(linkedinValue),
              has_email: Number(row?.has_email) === 1 || Boolean(emailValue),
              has_resume: Number(row?.has_resume) === 1 || Boolean(resumeValue || row?.documents_json),

              last_sync_label: row?.last_sync_label || "",
              last_sync_at: row?.last_sync_at || null,
              sort_order: Number(row?.sort_order) || idx + 1,
              lead_id: row?.lead_id ?? null,

              preferences_json: preferencesJson,
              pref_why_looking: row?.pref_why_looking || preferences?.whyLooking || "",
              pref_timeline: row?.pref_timeline || preferences?.timeline || "",
              created_at: row?.created_at || "",
              updated_at: row?.updated_at || "",
            };
          });

          return {
            rows: mappedRows,
            total: Number.isFinite(reportedTotal) && reportedTotal >= 0
              ? reportedTotal
              : mappedRows.length,
          };
        };

        const result = await loadCandidateRows();
        if (requestId !== candidateRequestId.current) return;
        setCandidates(result.rows);
        setCandidateTotal(result.total);
        if (listTab === "list" && !activeStatId && !hasCandidateClientFilters) setCandidateListTotal(result.total);
      } catch (err) {
        if (requestId !== candidateRequestId.current) return;
        console.error("Failed to fetch candidate data:", err);
        setCandidates([]);
        setCandidateTotal(0);
        setLoadError(err?.message || "Failed to load candidates");
      } finally {
        if (showLoading && requestId === candidateRequestId.current) setLoading(false);
      }
  }, [activeStatId, candidatePage, candidateRowsPerPage, hasCandidateClientFilters, listTab]);

  useEffect(() => {
    const refresh = () => { void fetchCandidatesOnly({ showLoading: false }); };
    window.addEventListener("rpo:activities-refresh", refresh);
    return () => window.removeEventListener("rpo:activities-refresh", refresh);
  }, [fetchCandidatesOnly]);

  const handleCandidatePageChange = useCallback((nextPage) => {
    setCandidatePage(Math.max(1, Number(nextPage) + 1));
  }, []);

  const handleCandidateRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setCandidateRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
    setCandidatePage(1);
  }, []);

  useEffect(() => {
    fetchCandidatesOnly();
  }, [fetchCandidatesOnly]);

  useEffect(() => {
    let active = true;
    const base = normalizeApiBase(API_BASE);

    fetch(`${base}/api/candidates?summary=pipeline-count`, {
      credentials: "include",
      headers: authHeaders(),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to load recruiting pipeline count (${response.status})`);
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        const total = Number(data?.count);
        setRecruitingPipelineTotal(Number.isFinite(total) ? Math.max(0, total) : null);
      })
      .catch((error) => {
        console.warn("Failed to fetch recruiting pipeline count:", error);
        if (active) setRecruitingPipelineTotal(null);
      });

    return () => {
      active = false;
    };
  }, [candidates]);

  useEffect(() => {
    let active = true;
    const base = normalizeApiBase(API_BASE);

    fetch(`${base}/api/candidates?summary=stats&${listTab === "pipeline" ? "has_job=1" : "funnel=status"}`, {
      credentials: "include",
      headers: authHeaders(),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to load candidate stats (${response.status})`);
        return response.json();
      })
      .then((data) => {
        if (active) setCandidateStats(data && typeof data === "object" ? data : null);
      })
      .catch((error) => {
        console.warn("Failed to fetch database-backed candidate stats:", error);
        if (active) setCandidateStats(null);
      });

    return () => {
      active = false;
    };
  }, [listTab, candidates]);

  // Tab badges represent their own complete datasets. In particular, the
  // pipeline request is filtered with `has_job=1` and must never overwrite the
  // Candidate List badge while that tab is inactive.
  useEffect(() => {
    let active = true;
    const base = normalizeApiBase(API_BASE);

    fetch(`${base}/api/candidates?summary=list-count`, {
      credentials: "include",
      headers: authHeaders(),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to load candidate list count (${response.status})`);
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        const total = Number(data?.count);
        setCandidateListTotal(Number.isFinite(total) ? Math.max(0, total) : null);
      })
      .catch((error) => {
        console.warn("Failed to fetch candidate list count:", error);
      });

    return () => {
      active = false;
    };
  }, [candidates]);

  const fetchCandidateDetails = useCallback(async (row) => {
    if (!row) return null;

    const base = normalizeApiBase(API_BASE);
    const candidateId = row?.candidate_id || row?.id;

    if (!candidateId) return row;

    const endpoints = [
      `${base}/api/candidates/${candidateId}`,
      `${base}/api/candidates/${candidateId}/profile`,
      `${base}/api/candidate/${candidateId}`,
      `${base}/api/candidate/${candidateId}/profile`,
    ];

    for (const endpoint of endpoints) {
      try {
        const res = await fetch(endpoint, {
          method: "GET",
          credentials: "include",
          headers: authHeaders(),
        });

        if (!res.ok) continue;

        const data = await res.json().catch(() => ({}));
        const candidateData = data?.candidate || data?.data || data;

        if (candidateData && typeof candidateData === "object") {
          const mergedPreferencesJson = stringifyCandidatePreferences(
            candidateData?.preferences_json || row?.preferences_json || row?.preferences
          );
          const mergedSourceRow = {
            ...row,
            ...candidateData,
            preferences_json: mergedPreferencesJson,
            experience_items: candidateData?.experience_items || row?.experience_items,
          };
          const mergedSkills = uniqueSorted([
            ...parseSkillsFromAny(row),
            ...parseSkillsFromAny(candidateData),
          ]);
          const explicitTags = extractCandidateExplicitTags(mergedSourceRow);
          const jobsSummary = buildCandidateJobsSummary(mergedSourceRow);
          const lastActivityAt = extractCandidateLastActivityAt(mergedSourceRow);
          const lastActivitySummary = extractCandidateLastActivitySummary(mergedSourceRow);

          const emailValue = pickFirst(getEmailValue(candidateData), getEmailValue(row));
          const linkedinValue = pickFirst(getLinkedinValue(candidateData), getLinkedinValue(row));
          const resumeValue = pickFirst(getResumeValue(candidateData), getResumeValue(row));
          const stageValue = pickFirst(getStageValue(candidateData), getStageValue(row));
          const statusValue =
            pickFirst(getStatusValue(candidateData), getStatusValue(row)) || "New";
          const mergedCompanyName = extractCurrentCompany(mergedSourceRow);

          return {
            ...row,
            ...candidateData,
            relation_id: row?.relation_id ?? row?.id,
            id: row?.id,
            candidate_id: row?.candidate_id ?? candidateData?.id ?? row?.id,

            full_name: pickFirst(candidateData?.full_name, candidateData?.name, row?.full_name, row?.candidate_name),
            candidate_name: pickFirst(candidateData?.full_name, candidateData?.name, row?.candidate_name, row?.full_name),

            title: pickFirst(candidateData?.title, candidateData?.candidate_title, row?.title, row?.candidate_title),
            candidate_title: pickFirst(candidateData?.candidate_title, candidateData?.title, row?.candidate_title, row?.title),

            avatar_url: pickFirst(candidateData?.avatar_url, candidateData?.profile_image, row?.avatar_url),

            email: emailValue,
            gmail: emailValue,

            phone: pickFirst(candidateData?.phone, candidateData?.phone_number, row?.phone, row?.phone_number),
            phone_number: pickFirst(candidateData?.phone_number, candidateData?.phone, row?.phone_number, row?.phone),

            location: pickFirst(candidateData?.location, candidateData?.candidate_location, row?.location, row?.candidate_location),
            candidate_location: pickFirst(candidateData?.candidate_location, candidateData?.location, row?.candidate_location, row?.location),

            status: statusValue,
            candidate_status: statusValue,

            stage: stageValue,
            candidate_stage: stageValue,

            recruiter_name: pickFirst(candidateData?.owner, candidateData?.recruiter_name, row?.recruiter_name, row?.owner),
            owner: pickFirst(candidateData?.owner, candidateData?.recruiter_name, row?.owner, row?.recruiter_name),

            company: pickFirst(
              candidateData?.company,
              candidateData?.candidate_company,
              candidateData?.current_company,
              candidateData?.currentCompany,
              candidateData?.company_client,
              row?.company,
              row?.candidate_company,
              row?.current_company,
              row?.company_client,
              mergedCompanyName
            ),
            candidate_company: pickFirst(
              candidateData?.candidate_company,
              candidateData?.company,
              candidateData?.current_company,
              candidateData?.currentCompany,
              candidateData?.company_client,
              row?.candidate_company,
              row?.company,
              row?.current_company,
              row?.company_client,
              mergedCompanyName
            ),
            company_client: pickFirst(
              candidateData?.company_client,
              candidateData?.current_company,
              candidateData?.currentCompany,
              row?.company_client,
              row?.current_company,
              mergedCompanyName
            ),
            current_company: pickFirst(
              candidateData?.current_company,
              candidateData?.currentCompany,
              candidateData?.company_client,
              row?.current_company,
              row?.currentCompany,
              row?.company_client,
              mergedCompanyName
            ),
            currentCompany: pickFirst(
              candidateData?.currentCompany,
              candidateData?.current_company,
              candidateData?.company_client,
              row?.currentCompany,
              row?.current_company,
              row?.company_client,
              mergedCompanyName
            ),

            linkedin_url: linkedinValue,
            resume_url: resumeValue,

            notes: pickFirst(candidateData?.notes, row?.notes),
            tags: explicitTags,
            candidate_tags: explicitTags,
            jobs_summary: jobsSummary,
            last_activity_at: lastActivityAt || null,
            last_activity_time: pickFirst(candidateData?.last_activity_time, row?.last_activity_time, lastActivityAt) || null,
            last_activity_detail: pickFirst(candidateData?.last_activity_detail, row?.last_activity_detail, lastActivitySummary),
            last_activity_message: pickFirst(candidateData?.last_activity_message, row?.last_activity_message, lastActivitySummary),

            preferences_json: mergedPreferencesJson,
            pref_why_looking: candidateData?.pref_why_looking || row?.pref_why_looking || "",
            pref_timeline: candidateData?.pref_timeline || row?.pref_timeline || "",

            skills: mergedSkills,
            skills_json:
              typeof candidateData?.skills_json === "string" && candidateData.skills_json.trim()
                ? candidateData.skills_json
                : JSON.stringify(mergedSkills),

            has_linkedin: Boolean(linkedinValue) || Boolean(row?.has_linkedin),
            has_email: Boolean(emailValue) || Boolean(row?.has_email),
            has_resume: Boolean(resumeValue || candidateData?.documents_json) || Boolean(row?.has_resume),
          };
        }
      } catch (err) {
        console.error("Failed to fetch candidate details:", err);
      }
    }

    return row;
  }, []);

  const deleteCandidateById = useCallback(async (candidateId) => {
    const base = normalizeApiBase(API_BASE);
    const endpoints = [
      `${base}/api/candidates/${candidateId}`,
      `${base}/api/candidate/${candidateId}`,
    ];

    let lastError = "";

    for (const endpoint of endpoints) {
      try {
        const res = await fetch(endpoint, {
          method: "DELETE",
          credentials: "include",
          headers: authHeaders(),
        });

        if (res.ok) {
          return true;
        }

        const text = await res.text().catch(() => "");
        lastError = `Delete failed for candidate ${candidateId} (${res.status}). ${text.slice(0, 200)}`;
      } catch (err) {
        lastError = err?.message || `Delete failed for candidate ${candidateId}`;
      }
    }

    throw new Error(lastError || `Delete failed for candidate ${candidateId}`);
  }, []);

  const handleDeleteSelected = useCallback(async (_event, options = {}) => {
    const normalizedSelectedIds = Array.isArray(selectedIds)
      ? selectedIds.map((id) => String(id)).filter(Boolean)
      : [];

    if (!normalizedSelectedIds.length || deleting || bulkActionBusy) return;

    if (!options?.confirmedByBulkActionsBar) {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete ${normalizedSelectedIds.length} candidate${normalizedSelectedIds.length > 1 ? "s" : ""}?`
      );

      if (!confirmDelete) return;
    }

    try {
      setDeleting(true);
      setLoadError("");

      const results = await Promise.allSettled(
        normalizedSelectedIds.map((id) => deleteCandidateById(id))
      );

      const successIds = [];
      const failedMessages = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successIds.push(normalizedSelectedIds[index]);
        } else {
          failedMessages.push(
            result.reason?.message || `Failed to delete candidate ${normalizedSelectedIds[index]}`
          );
        }
      });

      if (successIds.length) {
        setCandidates((prev) =>
          prev.filter((row) => !successIds.includes(String(row?.candidate_id || row?.id)))
        );
        setCandidateTotal((current) => Math.max(0, Number(current || 0) - successIds.length));
        setCandidateListTotal((current) =>
          current == null ? current : Math.max(0, Number(current) - successIds.length)
        );

        setSelectedIds((prev) =>
          (Array.isArray(prev) ? prev : []).filter(
            (id) => !successIds.includes(String(id))
          )
        );

        if (
          selectedCandidate &&
          successIds.includes(String(selectedCandidate?.candidate_id || selectedCandidate?.id))
        ) {
          setSelectedCandidate(null);
          setCandidateDrawerOpen(false);

          if (viewMode === "candidateTabsLayout") {
            setViewMode("list");
            navigate(
              {
                pathname: location.pathname,
                search: "",
              },
              { replace: true }
            );
          }
        }

        await fetchCandidatesOnly();
      }

      if (failedMessages.length) {
        setLoadError(failedMessages.join(" | "));
      }
    } catch (err) {
      console.error("Failed to delete selected candidates:", err);
      setLoadError(err?.message || "Failed to delete selected candidates");
    } finally {
      setDeleting(false);
    }
  }, [
    selectedIds,
    deleting,
    bulkActionBusy,
    deleteCandidateById,
    selectedCandidate,
    viewMode,
    navigate,
    location.pathname,
    fetchCandidatesOnly,
  ]);

  const openCandidateDrawer = useCallback(
    async (row) => {
      if (!row) return;

      setCandidateDrawerOpen(true);
      setCandidateDrawerLoading(true);
      setSelectedCandidate(row);

      try {
        const merged = await fetchCandidateDetails(row);
        setSelectedCandidate(merged || row);
      } catch (err) {
        console.error("Failed to open candidate drawer:", err);
        setSelectedCandidate(row);
      } finally {
        setCandidateDrawerLoading(false);
      }
    },
    [fetchCandidateDetails]
  );

  const handleShowMoreCandidate = useCallback(
    (tab = "details") => {
      if (!selectedCandidate) return;

      const safeTab = normalizeTab(tab);
      const candidateParam = selectedCandidate?.candidate_id || selectedCandidate?.id;

      setCandidateDrawerOpen(false);
      setDefaultTab(safeTab);
      setViewMode("candidateTabsLayout");

      navigate(
        {
          pathname: location.pathname,
          search: `?${new URLSearchParams({
            candidateId: String(candidateParam),
            tab: safeTab,
          }).toString()}`,
        },
        { replace: false }
      );
    },
    [selectedCandidate, navigate, location.pathname]
  );

  const openCandidateInTabs = useCallback(
    async (row, tab = "details") => {
      if (!row) return;

      const safeTab = normalizeTab(tab);
      const candidateParam = row?.candidate_id || row?.id;
      if (!candidateParam) return;

      setCandidateDrawerOpen(false);
      setSelectedCandidate(row);
      setDefaultTab(safeTab);
      setViewMode("candidateTabsLayout");

      navigate(
        {
          pathname: location.pathname,
          search: `?${new URLSearchParams({
            candidateId: String(candidateParam),
            tab: safeTab,
          }).toString()}`,
        },
        { replace: false }
      );

      try {
        const merged = await fetchCandidateDetails(row);
        setSelectedCandidate(merged || row);
      } catch (err) {
        console.error("Failed to open candidate in full view:", err);
      }
    },
    [fetchCandidateDetails, navigate, location.pathname]
  );

  useEffect(() => {
    if (sidebarResetToken) return;

    const qs = new URLSearchParams(location.search);
    const createdCandidateFromState = location.state?.createdCandidate;
    const routeActionToast = location.state?.actionToast;
    const stateCandidateId = location.state?.createdCandidateId;
    const candidateId = qs.get("candidateId") || (stateCandidateId ? String(stateCandidateId) : "");
    const tab = normalizeTab(qs.get("tab"));

    if (!candidateId) {
      if (viewMode === "candidateTabsLayout") {
        setDefaultTab("details");
        setViewMode("list");
      }
      return;
    }

    if (createdCandidateFromState) {
      const createdCandidate = {
        ...createdCandidateFromState,
        id:
          createdCandidateFromState?.id ??
          createdCandidateFromState?.candidate_id ??
          candidateId,
        candidate_id:
          createdCandidateFromState?.candidate_id ??
          createdCandidateFromState?.id ??
          candidateId,
      };

      if (
        String(createdCandidate.id) === String(candidateId) ||
        String(createdCandidate.candidate_id) === String(candidateId)
      ) {
        if (routeActionToast?.message) {
          setActionToast({
            open: true,
            severity: routeActionToast.severity || "info",
            message: routeActionToast.message,
          });
        }
        setSelectedCandidate((prev) => ({ ...(prev || {}), ...createdCandidate }));
        setDefaultTab(tab);
        setViewMode("candidateTabsLayout");
        setCandidateDrawerOpen(false);
        const nextState = { ...(location.state || {}) };
        delete nextState.createdCandidate;
        delete nextState.createdCandidateId;
        delete nextState.source;
        delete nextState.actionToast;
        navigate(
          {
            pathname: location.pathname,
            search: location.search,
          },
          {
            replace: true,
            state: Object.keys(nextState).length ? nextState : null,
          }
        );
        return;
      }
    }

    if (
      selectedCandidate &&
      (String(selectedCandidate.id) === String(candidateId) ||
        String(selectedCandidate.candidate_id) === String(candidateId))
    ) {
      setDefaultTab(tab);
      setViewMode("candidateTabsLayout");
      setCandidateDrawerOpen(false);
      return;
    }

    if (candidates.length) {
      const hit =
        candidates.find((r) => String(r.id) === String(candidateId)) ||
        candidates.find((r) => String(r.candidate_id) === String(candidateId));

      if (hit) {
        setSelectedCandidate(hit);
        setDefaultTab(tab);
        setViewMode("candidateTabsLayout");
        setCandidateDrawerOpen(false);
        return;
      }
    }

    let active = true;

    const fetchDeepLinkedCandidate = async () => {
      try {
        const mergedCandidate = await fetchCandidateDetails({
          id: candidateId,
          candidate_id: candidateId,
        });

        if (!active || !mergedCandidate) return;

        setSelectedCandidate(mergedCandidate);
        setDefaultTab(tab);
        setViewMode("candidateTabsLayout");
        setCandidateDrawerOpen(false);
      } catch (err) {
        console.error("Failed to open candidate from URL:", err);
      }
    };

    fetchDeepLinkedCandidate();

    return () => {
      active = false;
    };
  }, [
    location.pathname,
    location.search,
    location.state,
    candidates,
    selectedCandidate,
    fetchCandidateDetails,
    navigate,
    sidebarResetToken,
    viewMode,
  ]);

  const filterEndpoint = `/api/candidates?${new URLSearchParams({ search:search.trim(), ...(listTab === "pipeline" ? { has_job: "1" } : { funnel: "status" }), ...(activeStatId ? { stats_filter: activeStatId } : {}) })}`;
  const filterOptions = useMemo(() => {
    const names = uniqueSorted(candidates.map((c) => c.full_name));
    const skills = uniqueSorted(candidates.flatMap((c) => parseSkillsFromAny(c)));
    const locations = normalizeLocationFilterValues(candidates.flatMap((c) => extractCandidateLocations(c)));
    const owners = uniqueSorted(candidates.map((c) => c.recruiter_name || c.owner));
    const statuses = CANDIDATE_STATUS_VALUES;
    const stages = uniqueSorted(
      candidates.flatMap((c) => [
        normalizeCandidateStageFilterValue(c.candidate_stage || c.stage),
        normalizeCandidateStageFilterValue(getPipelineStageValue(c)),
      ])
    );
    const sources = uniqueSorted(candidates.map((c) => c.source || c.last_sync_label));

    return { names, skills, locations, owners, statuses, stages, sources };
  }, [candidates]);
  const candidateDetailFieldOptions = useMemo(
    () => ({
      locations: filterOptions.locations,
      skills: filterOptions.skills,
    }),
    [filterOptions.locations, filterOptions.skills]
  );

  const advancedSearchOptions = useMemo(
    () => ({
      jobTitles: uniqueSorted(candidates.flatMap((candidate) => extractCandidateJobFunctions(candidate))),
      skills: filterOptions.skills,
      locations: filterOptions.locations,
      owners: filterOptions.owners,
      statuses: filterOptions.statuses,
      stages: filterOptions.stages,
      sources: filterOptions.sources,
      languages: uniqueSorted(candidates.flatMap((candidate) => extractCandidateLanguages(candidate))),
      industries: uniqueSorted(candidates.flatMap((candidate) => extractCandidateIndustries(candidate))),
      schools: uniqueSorted(candidates.flatMap((candidate) => extractCandidateSchools(candidate))),
      fieldsOfStudy: uniqueSorted(candidates.flatMap((candidate) => extractCandidateFieldsOfStudy(candidate))),
      companies: uniqueSorted(
        candidates.flatMap((candidate) => [extractCurrentCompany(candidate), ...extractPastCompanies(candidate)])
      ),
      companySizes: uniqueSorted(candidates.map((candidate) => extractCandidateCompanySize(candidate))),
      companyTypes: uniqueSorted(candidates.flatMap((candidate) => extractCandidateCompanyTypes(candidate))),
      jobFunctions: uniqueSorted(candidates.flatMap((candidate) => extractCandidateJobFunctions(candidate))),
      recruitmentActivities: uniqueSorted(
        candidates.flatMap((candidate) => extractCandidateRecruitmentActivities(candidate))
      ),
      tags: uniqueSorted(candidates.flatMap((candidate) => extractCandidateTags(candidate))),
      pastApplications: uniqueSorted(
        candidates.flatMap((candidate) => extractCandidatePastApplications(candidate))
      ),
      notes: uniqueSorted(candidates.flatMap((candidate) => extractCandidateNoteValues(candidate))),
    }),
    [candidates, filterOptions]
  );

  const filterCandidateRows = useCallback((candidates, appliedFilters) => {
    let rows = [...candidates];

    // The Recruiting Pipeline request already uses `has_job=1` at the API.
    // Do not apply a second client-side association check here: a row can have
    // a valid association while its optional job summary is still being
    // hydrated, which previously made the first 10-row page render as five.

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((c) =>
        [
          c.full_name,
          c.title,
          c.status,
          c.company,
          c.location,
          c.stage,
          c.linkedin_url,
          c.email,
          c.gmail,
          c.recruiter_name,
          c.phone,
          c.pref_salary_range,
          c.pref_compensation,
          ...extractCandidateLocations(c),
          ...extractCandidateIndustries(c),
          ...(Array.isArray(c.skills) ? c.skills : []),
          ...parseSkillsFromAny(c),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    if (appliedFilters.aiRequest.trim()) {
      const q = appliedFilters.aiRequest.toLowerCase();
      rows = rows.filter((c) =>
        [
          c.full_name,
          c.title,
          c.company,
          c.location,
          c.email,
          c.gmail,
          c.notes,
          c.pref_why_looking,
          ...(Array.isArray(c.skills) ? c.skills : []),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    if (appliedFilters.names.length) {
      rows = rows.filter((c) => matchesAnySelectedValue(appliedFilters.names, [c.full_name]));
    }

    if (appliedFilters.skills.length) {
      rows = rows.filter((c) => {
        const rowSkills = parseSkillsFromAny(c);
        return matchesAnySelectedValue(appliedFilters.skills, rowSkills);
      });
    }

    if (appliedFilters.locations.length) {
      rows = rows.filter((c) => matchesAnySelectedValue(appliedFilters.locations, extractCandidateLocations(c)));
    }

    if (appliedFilters.owners.length) {
      rows = rows.filter((c) => matchesAnySelectedValue(appliedFilters.owners, [c.recruiter_name || c.owner]));
    }

    if (appliedFilters.statuses.length) {
      rows = rows.filter((c) => matchesAnySelectedValue(appliedFilters.statuses, [c.candidate_status || c.status]));
    }

    if (appliedFilters.stages.length) {
      rows = rows.filter((c) =>
        matchesAnySelectedValue(appliedFilters.stages, [
          normalizeCandidateStageFilterValue(c.candidate_stage || c.stage),
          normalizeCandidateStageFilterValue(getPipelineStageValue(c)),
        ])
      );
    }

    if (overviewMetric === "hired_this_month") {
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      rows = rows.filter((candidate) => {
        const timestamp = Date.parse(
          candidate?.created_at || candidate?.createdAt || candidate?.updated_at || candidate?.updatedAt || ""
        );
        return Number.isFinite(timestamp) && timestamp >= thirtyDaysAgo;
      });
    }

    if (appliedFilters.sources.length) {
      rows = rows.filter((c) => matchesAnySelectedValue(appliedFilters.sources, [c.source || c.last_sync_label]));
    }

    rows = applyAdvancedCandidateFilters(rows, advancedApplied);

    // Stage predicates are applied by the API before counting and pagination.

    return rows;
  }, [search, advancedApplied, overviewMetric]);

  const filteredCandidates = useMemo(() => filterCandidateRows(candidates, appliedFilters),
    [filterCandidateRows, candidates, appliedFilters]);

  const recruitingPipelineCount = recruitingPipelineTotal ?? candidates.filter(isCandidateInPipeline).length;

  const recommendedSequenceItems = useMemo(
    () => filteredCandidates.map((candidate) => buildCandidateSequenceItem(candidate)),
    [filteredCandidates]
  );

  const normalizedSelectedIds = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds.map((id) => String(id)).filter(Boolean) : []),
    [selectedIds]
  );

  const selectedIdSet = useMemo(
    () => new Set(normalizedSelectedIds),
    [normalizedSelectedIds]
  );

  const selectedCandidates = useMemo(
    () =>
      candidates.filter((row) =>
        selectedIdSet.has(String(row?.candidate_id || row?.id))
      ),
    [candidates, selectedIdSet]
  );
  const selectedFilteredCandidates = useMemo(
    () =>
      filteredCandidates.filter((row) =>
        selectedIdSet.has(String(row?.candidate_id || row?.id))
      ),
    [filteredCandidates, selectedIdSet]
  );

  const selectedCount = selectedCandidates.length;
  const selectedSequenceItems = useMemo(
    () => selectedCandidates.map((candidate) => buildCandidateSequenceItem(candidate)),
    [selectedCandidates]
  );
  const sequenceDialogItems = useMemo(
    () =>
      Array.isArray(sequenceDialogItemsOverride)
        ? sequenceDialogItemsOverride
        : selectedSequenceItems,
    [sequenceDialogItemsOverride, selectedSequenceItems]
  );

  const showActionToast = useCallback((message, severity = "success") => {
    setActionToast({
      open: true,
      severity,
      message,
    });
  }, []);

  const closeActionToast = useCallback(() => {
    setActionToast((prev) => ({ ...prev, open: false }));
  }, []);

  const agentTaskCandidates = useMemo(
    () =>
      selectedFilteredCandidates.length ? selectedFilteredCandidates : filteredCandidates,
    [filteredCandidates, selectedFilteredCandidates]
  );

  const handleOpenAddCandidate = useCallback(() => {
    setIsAddCandidateOpen(true);
  }, []);

  const handleCloseAddCandidate = useCallback(() => {
    setIsAddCandidateOpen(false);
  }, []);

  const handleCandidateCreated = useCallback(async () => {
    setIsAddCandidateOpen(false);
    await fetchCandidatesOnly();
  }, [fetchCandidatesOnly]);

  const handleCloseAgentDialog = useCallback(() => {
    setIsAgentDialogOpen(false);
    setAgentDialogInitialPrompt("");
    setAgentDialogSubmitOnOpen(false);
  }, []);

  const handleOpenLaunchAgentMenu = useCallback((event) => {
    setLaunchAgentMenuAnchorEl(event.currentTarget);
  }, []);

  const handleOpenCandidatePitchPackage = useCallback((candidateRows) => {
    const pitchCandidates = Array.isArray(candidateRows) ? candidateRows.filter(Boolean) : [];
    if (!pitchCandidates.length) {
      showActionToast("Select at least one candidate to build a pitch package.", "info");
      return;
    }
    setCandidatePitchPackageDialog({ open: true, candidates: pitchCandidates });
  }, [showActionToast]);

  const handleCandidatePitchPackageCreated = useCallback((packageDetails) => {
    const candidateCount = packageDetails?.candidates?.length || 0;
    const recipient = packageDetails?.recipientEmail || "the recipient";
    showActionToast(
      `Pitch package sent for ${candidateCount} candidate${candidateCount === 1 ? "" : "s"} to ${recipient}.`
    );
  }, [showActionToast]);

  const handleCloseLaunchAgentMenu = useCallback(() => {
    setLaunchAgentMenuAnchorEl(null);
  }, []);

  const startCandidateProfileEnrichment = useCallback((candidateRows) => {
    const candidateIds = (Array.isArray(candidateRows) ? candidateRows : [])
      .map((candidate) => String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "").trim())
      .filter(Boolean);
    if (!candidateIds.length) return;

    setActiveEnrichmentCandidateIds(candidateIds);
    setCandidateEnrichmentStatusById((current) => ({
      ...current,
      ...Object.fromEntries(candidateIds.map((id) => [id, "in_progress"])),
    }));
  }, []);

  const handleCandidateAgentActivity = useCallback(() => {
    if (!activeEnrichmentCandidateIds.length) return;
    setCandidateEnrichmentStatusById((current) => ({
      ...current,
      ...Object.fromEntries(activeEnrichmentCandidateIds.map((id) => [id, "complete"])),
    }));
    setActiveEnrichmentCandidateIds([]);
  }, [activeEnrichmentCandidateIds]);

  const runCandidateProfileEnrichment = useCallback(
    async (candidateRows, scopeLabel) => {
      const candidatesToEnrich = Array.isArray(candidateRows) ? candidateRows.filter(Boolean) : [];
      if (!candidatesToEnrich.length) return;

      startCandidateProfileEnrichment(candidatesToEnrich);
      showActionToast(
        `Enriching ${candidatesToEnrich.length} candidate profile${candidatesToEnrich.length === 1 ? "" : "s"} in the background.`
      );

      try {
        const formData = new FormData();
        formData.append("mode", "chat");
        formData.append(
          "message",
          buildCandidateAgentTaskPrompt({
            taskValue: "enrich-profile",
            candidates: candidatesToEnrich,
            scopeLabel,
          })
        );
        const response = await fetch(`${API_BASE}/api/ai-agent/respond`, {
          method: "POST",
          credentials: "include",
          headers: authHeaders(),
          body: formData,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Unable to enrich candidate profiles.");
        }

        const candidateIds = candidatesToEnrich
          .map((candidate) => String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "").trim())
          .filter(Boolean);
        setCandidateEnrichmentStatusById((current) => ({
          ...current,
          ...Object.fromEntries(candidateIds.map((id) => [id, "complete"])),
        }));
        setActiveEnrichmentCandidateIds([]);
        showActionToast(
          `Candidate profile enrichment completed for ${candidateIds.length} candidate${candidateIds.length === 1 ? "" : "s"}.`
        );
      } catch (error) {
        const candidateIds = candidatesToEnrich
          .map((candidate) => String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "").trim())
          .filter(Boolean);
        setCandidateEnrichmentStatusById((current) => ({
          ...current,
          ...Object.fromEntries(candidateIds.map((id) => [id, "failed"])),
        }));
        setActiveEnrichmentCandidateIds([]);
        showActionToast(error?.message || "Unable to enrich candidate profiles.", "error");
      }
    },
    [showActionToast, startCandidateProfileEnrichment]
  );

  const handleLaunchCandidateListAgentTask = useCallback((taskValue) => {
    if (!agentTaskCandidates.length) {
      showActionToast(
        "No candidates are available for an agent task. Adjust filters or select candidates first.",
        "info"
      );
      return;
    }

    const scopeLabel = selectedFilteredCandidates.length
      ? "selected candidates"
      : "filtered candidates";
    const selectedTask =
      CANDIDATE_AGENT_TASK_OPTIONS.find((option) => option.value === taskValue) || null;
    const taskLabel = selectedTask?.label || "Launch Agent";
    if (taskValue === "build-pitch-package") {
      handleOpenCandidatePitchPackage(agentTaskCandidates);
      return;
    }
    if (taskValue === "enrich-profile") {
      void runCandidateProfileEnrichment(agentTaskCandidates, scopeLabel);
      return;
    }
    if (taskValue === "build-sales-sequence") {
      setSequenceDialogItemsOverride(
        agentTaskCandidates.map((candidate) => buildCandidateSequenceItem(candidate))
      );
      setSequenceDialogInitialCampaignType("sales");
      setIsAddToSequenceOpen(true);
      return;
    }
    setAgentDialogInitialPrompt(
      buildCandidateAgentTaskPrompt({
        taskValue,
        candidates: agentTaskCandidates,
        scopeLabel,
      })
    );
    setAgentDialogSubmitOnOpen(true);
    setIsAgentDialogOpen(true);
    showActionToast(
      `Launching ${taskLabel} for ${agentTaskCandidates.length} ${scopeLabel}.`
    );
  }, [agentTaskCandidates, handleOpenCandidatePitchPackage, runCandidateProfileEnrichment, selectedFilteredCandidates.length, showActionToast]);

  const handleLaunchCandidateAgentTask = useCallback(
    (taskValue) => {
      if (!selectedCandidates.length) {
        showActionToast("Select candidates first to launch an AI Agent task.", "info");
        return;
      }

      const selectedTask =
        CANDIDATE_AGENT_TASK_OPTIONS.find((option) => option.value === taskValue) || null;
      const taskLabel = selectedTask?.label || "Launch Agent";
      if (taskValue === "build-pitch-package") {
        handleOpenCandidatePitchPackage(selectedCandidates);
        return;
      }
      if (taskValue === "enrich-profile") {
        void runCandidateProfileEnrichment(selectedCandidates, "selected candidates");
        return;
      }
      if (taskValue === "build-sales-sequence") {
        setSequenceDialogItemsOverride(
          selectedCandidates.map((candidate) => buildCandidateSequenceItem(candidate))
        );
        setSequenceDialogInitialCampaignType("sales");
        setIsAddToSequenceOpen(true);
        return;
      }

      setAgentDialogInitialPrompt(
        buildCandidateAgentTaskPrompt({
          taskValue,
          candidates: selectedCandidates,
          scopeLabel: "selected candidates",
        })
      );
      setAgentDialogSubmitOnOpen(true);
      setIsAgentDialogOpen(true);
      showActionToast(
        `Launching ${taskLabel} for ${selectedCandidates.length} selected candidate${selectedCandidates.length === 1 ? "" : "s"}.`
      );
    },
    [handleOpenCandidatePitchPackage, runCandidateProfileEnrichment, selectedCandidates, showActionToast]
  );

  const handleExportCsv = useCallback(() => {
    const exportRows = selectedCandidates.length ? selectedCandidates : filteredCandidates;
    if (!exportRows.length) {
      showActionToast("No candidates are available to export.", "info");
      return;
    }

    downloadCandidatesCsv(exportRows);
    showActionToast(
      `Exported ${exportRows.length} candidate${exportRows.length === 1 ? "" : "s"} to CSV.`
    );
  }, [filteredCandidates, selectedCandidates, showActionToast]);

  const handleAddToTemplate = useCallback(() => {
    showActionToast(
      selectedCount
        ? `Add to Template is ready for ${selectedCount} selected candidate${selectedCount === 1 ? "" : "s"}.`
        : "Select candidates first to add them to a template.",
      selectedCount ? "success" : "info"
    );
  }, [selectedCount, showActionToast]);

  const handleSendEmail = useCallback(() => {
    showActionToast(
      selectedCount
        ? `Send Email is ready for ${selectedCount} selected candidate${selectedCount === 1 ? "" : "s"}.`
        : "Select candidates first to send email.",
      selectedCount ? "success" : "info"
    );
  }, [selectedCount, showActionToast]);

  const handleGenerateClientSubmission = useCallback(() => {
    showActionToast(
      selectedCount
        ? `Client submission generation is ready for ${selectedCount} selected candidate${selectedCount === 1 ? "" : "s"}.`
        : "Select candidates first to generate a client submission.",
      selectedCount ? "success" : "info"
    );
  }, [selectedCount, showActionToast]);

  const handleAddToJob = useCallback(() => {
    showActionToast(
      selectedCount
        ? `Add to Job is ready for ${selectedCount} selected candidate${selectedCount === 1 ? "" : "s"}.`
        : "Select candidates first to add them to a job.",
      selectedCount ? "success" : "info"
    );
  }, [selectedCount, showActionToast]);

  const bulkMoreActionOptions = useMemo(
    () => [
      {
        value: "assign-sourcer",
        label: "Assign Sourcer",
        disabled: selectedCount === 0,
      },
      {
        value: "add-tags",
        label: "Add Tags",
        disabled: selectedCount === 0,
      },
      {
        value: "add-to-template",
        label: "Add to Template",
        disabled: selectedCount === 0,
      },
      {
        value: "add-to-sequence",
        label: "Add to Sequence",
        disabled: selectedCount === 0,
      },
      {
        value: "send-email",
        label: "Send Email",
        disabled: selectedCount === 0,
      },
      {
        value: "generate-client-submission",
        label: "Generate Client Submission",
        disabled: selectedCount === 0,
      },
      {
        value: "export-csv",
        label: "Export CSV",
      },
      {
        value: "archive",
        label: "Archive",
        disabled: selectedCount === 0,
      },
    ],
    [selectedCount]
  );

  const handleMatchToJob = useCallback(() => {
    showActionToast(
      selectedCount
        ? `Match to Job is ready for ${selectedCount} selected candidate${selectedCount === 1 ? "" : "s"}.`
        : "Select candidates first to match them to a job.",
      selectedCount ? "success" : "info"
    );
  }, [selectedCount, showActionToast]);

  const openAssociatedJob = useCallback(
    (jobId) => {
      if (jobId === undefined || jobId === null || String(jobId).trim() === "") return;
      navigate(`/jobs?jobId=${encodeURIComponent(String(jobId))}&tab=details`);
    },
    [navigate]
  );

  const handleOpenRowMenu = useCallback((event, row) => {
    event.stopPropagation?.();
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuCandidate(row);
  }, []);

  const handleCloseRowMenu = useCallback(() => {
    setRowMenuAnchorEl(null);
    setRowMenuCandidate(null);
  }, []);

  const handleOpenResumePreview = useCallback((resume) => {
    if (!resume?.url) return;
    setResumePreview(resume);
  }, []);

  const topStats = useMemo(() => {
    return (listTab === "pipeline" ? RECRUITING_STAGES : CANDIDATE_STATUS_STATS).map((item) => ({
      id: item.id,
      value: String(
        Number.isFinite(Number(candidateStats?.[item.id]))
          ? Math.max(0, Number(candidateStats[item.id]))
          : 0
      ),
      label: item.label,
      ...recruitingMetricColors(item.id),
    }));
  }, [candidateStats, listTab]);

  const columns = useMemo(
    () => [
      {
        id: "candidate_name",
        label: "Name",
        minWidth: 220,
        sortable: true,
        renderCell: (row) => {
          const displayName = row.full_name || EMPTY_VALUE;
          const enrichmentStatus =
            candidateEnrichmentStatusById[
              String(row?.id ?? row?.candidate_id ?? row?.candidateId ?? "").trim()
            ];

          return (
            <Box
              onClick={() => openCandidateDrawer(row)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                cursor: "pointer",
              }}
            >
              <Avatar
                src={row.avatar_url || ""}
                alt={displayName}
                sx={{ width: 30, height: 30 }}
              >
                {(displayName?.[0] || "?").toUpperCase()}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.2,
                    color: TEXT,
                    fontWeight: 500,
                  }}
                  noWrap
                >
                  {displayName}
                </Typography>
                {enrichmentStatus ? (
                  <Box sx={{ mt: 0.55, minWidth: 130 }}>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        color:
                          enrichmentStatus === "complete"
                            ? "#16A34A"
                            : enrichmentStatus === "failed"
                              ? "#DC2626"
                              : PURPLE,
                        lineHeight: 1.2,
                      }}
                    >
                      {enrichmentStatus === "complete"
                        ? "Profile enriched"
                        : enrichmentStatus === "failed"
                          ? "Profile enrichment failed"
                          : "Enriching profile…"}
                    </Typography>
                    {enrichmentStatus === "in_progress" ? (
                      <LinearProgress
                        sx={{
                          mt: 0.4,
                          height: 3,
                          borderRadius: 99,
                          bgcolor: "#F3E8FF",
                          "& .MuiLinearProgress-bar": { bgcolor: PURPLE },
                        }}
                      />
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Box>
          );
        },
      },
      listTab === "pipeline" ? {
        id: "job_company_name",
        label: "Company Name",
        minWidth: 180,
        sortable: true,
        renderCell: (row) => <PipelineCompanyTableCell row={row} />,
      } : {
        id: "current_company",
        label: "Company Name",
        minWidth: 150,
        sortable: true,
        renderCell: (row) => <CompanyTableCell row={row} />,
      },
      {
        id: "candidate_title",
        label: "Current Title",
        minWidth: 160,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: "#475467" }} noWrap>
            {row.candidate_title || row.title || EMPTY_VALUE}
          </Typography>
        ),
      },
      {
        id: "candidate_location",
        label: "Location",
        minWidth: 110,
        sortable: true,
        renderCell: (row) => (
          <TableLocationValue
            row={row}
            fallback={EMPTY_VALUE}
            textSx={{ fontSize: 14, color: "#475467" }}
          />
        ),
      },
      {
        id: "candidate_status",
        label: "Status",
        minWidth: 125,
        sortable: true,
        renderCell: (row) => <StatusChip value={row.candidate_status} />,
      },
      {
        id: "jobs_summary",
        label: "Job",
        minWidth: 150,
        sortable: false,
        renderCell: (row) => <JobsCell row={row} onOpenJob={openAssociatedJob} />,
      },
      {
        id: "candidate_stage",
        label: listTab === "pipeline" ? "Current Stage" : "Latest Stage",
        minWidth: 115,
        sortable: true,
        renderCell: (row) => (
          <StageText value={listTab === "pipeline" ? getPipelineStageValue(row) : CORE_CANDIDATE_STAGES.find(item => item.id === coreCandidateStage(getStageValue(row)))?.label || getStageValue(row)} />
        ),
      },
      {
        id: "contact_info",
        label: "Contact Info",
        minWidth: 150,
        sortable: false,
        renderCell: (row) => (
          <ContactInfoCell row={row} onOpenResume={handleOpenResumePreview} />
        ),
      },
      {
        id: "last_activity_at",
        label: "Last Activity",
        minWidth: 170,
        sortable: true,
        renderCell: (row) => <LastActivityCell row={row} />,
      },
      {
        id: "owner",
        label: "Owner",
        minWidth: 120,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: "#475467", whiteSpace: "nowrap" }}>
            {row.owner || row.recruiter_name || row.assigned_team_member || EMPTY_VALUE}
          </Typography>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        minWidth: 72,
        sortable: false,
        align: "center",
        renderCell: (row) => (
          <IconButton
            size="small"
            aria-label={`Open actions for ${row?.candidate_name || row?.full_name || "candidate"}`}
            sx={{ color: "#98A2B3" }}
            onClick={(event) => handleOpenRowMenu(event, row)}
          >
            <MoreVertIcon sx={{ fontSize: 18, color: "currentColor" }} />
          </IconButton>
        ),
      },
    ],
    [candidateEnrichmentStatusById, handleOpenResumePreview, handleOpenRowMenu, listTab, openAssociatedJob, openCandidateDrawer]
  );

  const renderRow = useCallback(
    (row) => (
      <>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align || "left"}
            sx={{
              minWidth: column.minWidth,
              py: 1.35,
              borderBottom: `1px solid ${BORDER}`,
              verticalAlign: "middle",
            }}
          >
            {column.renderCell ? column.renderCell(row) : row[column.id]}
          </TableCell>
        ))}
      </>
    ),
    [columns]
  );

  const handleOpenFilterDrawer = useCallback(() => {
    setFilterDraft(appliedFilters);
    setFilterDrawerOpen(true);
  }, [appliedFilters]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(filterDraft);
    setCandidatePage(1);
    setFilterDrawerOpen(false);
  }, [filterDraft]);

  const handleAdvanceSearch = useCallback(() => {
    const normalizedBasicFilters = {
      ...createEmptyFilterDraft(),
      aiRequest: cleanText(filterDraft.aiRequest),
      names: uniqueSorted(filterDraft.names),
      skills: uniqueSorted(filterDraft.skills),
      locations: normalizeLocationList(filterDraft.locations),
      owners: uniqueSorted(filterDraft.owners),
      statuses: uniqueSorted(filterDraft.statuses),
      stages: uniqueSorted(filterDraft.stages),
      sources: uniqueSorted(filterDraft.sources),
    };

    setFilterDraft(normalizedBasicFilters);
    setAppliedFilters(normalizedBasicFilters);
    setAdvancedApplied((prev) => mergeBasicIntoAdvancedCandidateFilters(normalizedBasicFilters, prev));
    setCandidatePage(1);
    setFilterDrawerOpen(false);
    setShowAdvancedSearch(true);
  }, [filterDraft]);

  const handleCloseAdvancedSearch = useCallback(() => {
    setShowAdvancedSearch(false);
  }, []);

  const handleApplyAdvancedSearch = useCallback((advancedPayload) => {
    const normalizedAdvanced = normalizeCandidateAdvancedFilters(advancedPayload);
    const nextBasicFilters = advancedCandidateFiltersToBasicFilters(
      normalizedAdvanced,
      createEmptyFilterDraft()
    );

    setAdvancedApplied(normalizedAdvanced);
    setFilterDraft(nextBasicFilters);
    setAppliedFilters(nextBasicFilters);
    setCandidatePage(1);
    setShowAdvancedSearch(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    const empty = createEmptyFilterDraft();
    setFilterDraft(empty);
    setAppliedFilters(empty);
    setAdvancedApplied(null);
    setCandidatePage(1);
  }, []);

  const updateAppliedFiltersFromResults = useCallback((updater) => {
    setAppliedFilters((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setFilterDraft(next);
      setAdvancedApplied((current) =>
        current ? mergeBasicIntoAdvancedCandidateFilters(next, current) : current
      );
      return next;
    });
    setCandidatePage(1);
  }, []);

  const handleRemoveAppliedFilterPill = useCallback(
    (field, value) => {
      updateAppliedFiltersFromResults((prev) => {
        if (field === "aiRequest") {
          return { ...prev, aiRequest: "" };
        }

        return {
          ...prev,
          [field]: removeFilterValueFromList(prev[field], value),
        };
      });
    },
    [updateAppliedFiltersFromResults]
  );

  const resultsFilterPills = useMemo(() => {
    const pills = [];

    const appendTextPill = (field, value, prefix = "") => {
      const label = cleanText(value);
      if (!label) return;

      pills.push({
        key: `${field}:${normalizeFilterTerm(label)}`,
        label: `${prefix}${label}`,
        onDelete: () => handleRemoveAppliedFilterPill(field, label),
      });
    };

    const appendArrayPills = (field, values = []) => {
      (values || []).forEach((value) => {
        const label = cleanText(value);
        if (!label) return;

        pills.push({
          key: `${field}:${normalizeFilterTerm(label)}`,
          label,
          onDelete: () => handleRemoveAppliedFilterPill(field, label),
        });
      });
    };

    appendTextPill("aiRequest", appliedFilters.aiRequest, "AI: ");
    appendArrayPills("names", appliedFilters.names);
    appendArrayPills("skills", appliedFilters.skills);
    appendArrayPills("locations", appliedFilters.locations);
    appendArrayPills("owners", appliedFilters.owners);
    appendArrayPills("statuses", appliedFilters.statuses);
    appendArrayPills("stages", appliedFilters.stages);
    appendArrayPills("sources", appliedFilters.sources);

    return pills;
  }, [appliedFilters, handleRemoveAppliedFilterPill]);

  const handleOpenAddToSequence = useCallback((items = null) => {
    setSequenceDialogItemsOverride(Array.isArray(items) ? items : null);
    setSequenceDialogInitialCampaignType("recruiting");
    setIsAddToSequenceOpen(true);
  }, []);

  const handleCloseAddToSequence = useCallback(() => {
    setIsAddToSequenceOpen(false);
    setSequenceDialogItemsOverride(null);
    setSequenceDialogInitialCampaignType("recruiting");
  }, []);

  const handleBulkAddToSequence = useCallback(() => {
    if (!selectedCount) {
      showActionToast("Select candidates first to add them to a sequence.", "info");
      return;
    }

    handleOpenAddToSequence();
  }, [handleOpenAddToSequence, selectedCount, showActionToast]);

  const saveCandidateRowUpdate = useCallback(
    async (row, profilePatch, options = {}) => {
      const candidateId = row?.candidate_id || row?.id;
      if (!candidateId) {
        throw new Error("Candidate ID is missing.");
      }

      const refreshAfterSave = options?.refreshAfterSave !== false;
      const refreshSelectedCandidate = options?.refreshSelectedCandidate !== false;
      const payload = {
        id: candidateId,
        candidate_id: candidateId,
        ...profilePatch,
      };

      const base = normalizeApiBase(API_BASE);
      const endpoints = [
        `${base}/api/candidates/${candidateId}`,
        `${base}/api/candidates/${candidateId}/profile`,
        `${base}/api/candidates/${candidateId}/details`,
        `${base}/api/candidate/${candidateId}`,
        `${base}/api/candidate/${candidateId}/profile`,
        `${base}/candidates/${candidateId}`,
        `${base}/candidates/${candidateId}/profile`,
        `${base}/candidate/${candidateId}`,
        `${base}/candidate/${candidateId}/profile`,
      ];
      const requestBodies = [
        payload,
        { candidate: payload },
        { profile: payload },
        { id: candidateId, candidate: payload },
        { id: candidateId, profile: payload },
        { candidateId, candidate: payload },
        { candidateId, profile: payload },
      ];

      let lastError = "";

      for (const endpoint of endpoints) {
        for (const method of ["PUT", "PATCH"]) {
          for (const body of requestBodies) {
            try {
              const res = await fetch(endpoint, {
                method,
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  ...authHeaders(),
                },
                body: JSON.stringify(body),
              });
              const data = await res.json().catch(() => null);

              if (!res.ok) {
                lastError =
                  data?.error ||
                  data?.message ||
                  `Failed to update candidate (${res.status}).`;
                continue;
              }

              try {
                if (refreshAfterSave) {
                  await fetchCandidatesOnly();
                }

                if (
                  refreshSelectedCandidate &&
                  selectedCandidate &&
                  String(selectedCandidate?.candidate_id || selectedCandidate?.id) === String(candidateId)
                ) {
                  const merged = await fetchCandidateDetails({
                    ...row,
                    ...payload,
                    ...(data?.candidate || data?.data || data?.item || {}),
                  });
                  setSelectedCandidate(merged || { ...row, ...payload });
                }
              } catch (refreshError) {
                console.error("Candidate update succeeded, but refresh failed:", refreshError);
              }

              return data?.candidate || data?.data || data?.item || payload;
            } catch (err) {
              lastError = err?.message || "Failed to update candidate.";
            }
          }
        }
      }

      throw new Error(lastError || "Could not save candidate update.");
    },
    [fetchCandidatesOnly, fetchCandidateDetails, selectedCandidate]
  );

  const applyBulkCandidateUpdates = useCallback(
    async (rows, buildPatch) => {
      const rowsToUpdate = Array.isArray(rows) ? rows.filter(Boolean) : [];
      if (!rowsToUpdate.length) {
        return { updatedCount: 0, failedMessages: [] };
      }

      const resolvePatch = typeof buildPatch === "function" ? buildPatch : () => buildPatch;
      setBulkActionBusy(true);

      try {
        const results = await Promise.allSettled(
          rowsToUpdate.map((row) =>
            saveCandidateRowUpdate(row, resolvePatch(row), {
              refreshAfterSave: false,
              refreshSelectedCandidate: false,
            })
          )
        );

        const updatedRows = [];
        const failedMessages = [];

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            updatedRows.push(rowsToUpdate[index]);
            return;
          }

          failedMessages.push(
            result.reason?.message ||
              `Failed to update ${rowsToUpdate[index]?.full_name || rowsToUpdate[index]?.candidate_name || "candidate"}.`
          );
        });

        if (updatedRows.length) {
          try {
            await fetchCandidatesOnly();

            if (selectedCandidate) {
              const activeCandidateId = String(
                selectedCandidate?.candidate_id || selectedCandidate?.id || ""
              );
              const activeWasUpdated =
                activeCandidateId &&
                updatedRows.some(
                  (row) => String(row?.candidate_id || row?.id || "") === activeCandidateId
                );

              if (activeWasUpdated) {
                const merged = await fetchCandidateDetails({
                  ...selectedCandidate,
                  candidate_id: selectedCandidate?.candidate_id || activeCandidateId,
                  id: selectedCandidate?.id || activeCandidateId,
                });
                if (merged) setSelectedCandidate(merged);
              }
            }
          } catch (refreshError) {
            console.error("Bulk candidate update refresh failed:", refreshError);
          }
        }

        return {
          updatedCount: updatedRows.length,
          failedMessages,
        };
      } finally {
        setBulkActionBusy(false);
      }
    },
    [fetchCandidateDetails, fetchCandidatesOnly, saveCandidateRowUpdate, selectedCandidate]
  );

  const handleBulkMoveStage = useCallback(
    async (stage) => {
      const nextStage = cleanText(stage);
      if (!nextStage || !selectedCandidates.length || bulkActionBusy || deleting || statusSaving) {
        return;
      }

      const result = await applyBulkCandidateUpdates(selectedCandidates, {
        stage: nextStage,
        candidate_stage: nextStage,
      });

      if (result.updatedCount) {
        showActionToast(
          `Moved ${result.updatedCount} candidate${result.updatedCount === 1 ? "" : "s"} to "${nextStage}".`
        );
      }

      if (result.failedMessages.length) {
        setLoadError(result.failedMessages.join(" | "));
        showActionToast(result.failedMessages[0], "error");
      }
    },
    [
      applyBulkCandidateUpdates,
      bulkActionBusy,
      deleting,
      selectedCandidates,
      showActionToast,
      statusSaving,
    ]
  );

  const handleAssignRecruiter = useCallback(async (recruiter) => {
    if (!selectedCandidates.length || bulkActionBusy || deleting || statusSaving) {
      showActionToast("Select candidates first to assign a recruiter.", "info");
      return;
    }

    const recruiterName = cleanText(recruiter?.name || recruiter);
    if (!recruiterName) {
      showActionToast("Choose a recruiter first.", "info");
      return;
    }

    const result = await applyBulkCandidateUpdates(selectedCandidates, {
      owner: recruiterName,
      recruiter_name: recruiterName,
    });

    if (result.updatedCount) {
      showActionToast(
        `Assigned recruiter "${recruiterName}" to ${result.updatedCount} candidate${result.updatedCount === 1 ? "" : "s"}.`
      );
    }

    if (result.failedMessages.length) {
      setLoadError(result.failedMessages.join(" | "));
      showActionToast(result.failedMessages[0], "error");
    }
  }, [
    applyBulkCandidateUpdates,
    bulkActionBusy,
    deleting,
    selectedCandidates,
    showActionToast,
    statusSaving,
  ]);

  const handleAssignSourcer = useCallback(async () => {
    if (!selectedCandidates.length || bulkActionBusy || deleting || statusSaving) {
      showActionToast("Select candidates first to assign a sourcer.", "info");
      return;
    }

    const nextSourcer = window.prompt("Assign sourcer to selected candidates:", "");
    if (nextSourcer === null) return;

    const sourcerName = cleanText(nextSourcer);
    if (!sourcerName) {
      showActionToast("Enter a sourcer name first.", "info");
      return;
    }

    const result = await applyBulkCandidateUpdates(selectedCandidates, (candidate) => {
      const preferences = getCandidatePreferences(candidate);
      return {
        sourcer_name: sourcerName,
        sourcerName,
        preferences_json: stringifyCandidatePreferences({
          ...preferences,
          sourcer_name: sourcerName,
          sourcerName,
        }),
      };
    });

    if (result.updatedCount) {
      showActionToast(
        `Assigned sourcer "${sourcerName}" to ${result.updatedCount} candidate${result.updatedCount === 1 ? "" : "s"}.`
      );
    }

    if (result.failedMessages.length) {
      setLoadError(result.failedMessages.join(" | "));
      showActionToast(result.failedMessages[0], "error");
    }
  }, [
    applyBulkCandidateUpdates,
    bulkActionBusy,
    deleting,
    selectedCandidates,
    showActionToast,
    statusSaving,
  ]);

  const handleBulkAddTags = useCallback(async () => {
    if (!selectedCandidates.length || bulkActionBusy || deleting || statusSaving) {
      showActionToast("Select candidates first to add tags.", "info");
      return;
    }

    const nextTags = window.prompt("Enter tags for the selected candidates (comma-separated):", "");
    if (nextTags === null) return;

    const tagList = uniqueSorted(asArray(nextTags));
    const result = await applyBulkCandidateUpdates(selectedCandidates, (candidate) => {
      const preferences = getCandidatePreferences(candidate);
      return {
        tags: tagList,
        candidate_tags: tagList,
        tag_list: tagList,
        tagList,
        preferences_json: stringifyCandidatePreferences({
          ...preferences,
          tags: tagList,
          tagList,
        }),
      };
    });

    if (result.updatedCount) {
      showActionToast(
        tagList.length
          ? `Updated tags for ${result.updatedCount} candidate${result.updatedCount === 1 ? "" : "s"}.`
          : `Cleared tags for ${result.updatedCount} candidate${result.updatedCount === 1 ? "" : "s"}.`
      );
    }

    if (result.failedMessages.length) {
      setLoadError(result.failedMessages.join(" | "));
      showActionToast(result.failedMessages[0], "error");
    }
  }, [
    applyBulkCandidateUpdates,
    bulkActionBusy,
    deleting,
    selectedCandidates,
    showActionToast,
    statusSaving,
  ]);

  const handleViewProfileAction = useCallback(async () => {
    const candidate = rowMenuCandidate;
    handleCloseRowMenu();
    if (!candidate) return;
    await openCandidateDrawer(candidate);
  }, [rowMenuCandidate, handleCloseRowMenu, openCandidateDrawer]);

  const handleEditCandidateAction = useCallback(async () => {
    const candidate = rowMenuCandidate;
    handleCloseRowMenu();
    if (!candidate) return;
    await openCandidateInTabs(candidate, "details");
  }, [rowMenuCandidate, handleCloseRowMenu, openCandidateInTabs]);

  const handleShareCandidateAction = useCallback(async () => {
    const candidate = rowMenuCandidate;
    handleCloseRowMenu();
    if (!candidate) return;

    try {
      await createAndCopyPublicShareLink({
        apiBase: API_BASE,
        payload: buildPublicSharePayload("candidate", candidate),
      });
      showActionToast(SHARE_LINK_COPIED_MESSAGE);
    } catch (error) {
      console.error("Failed to share candidate:", error);
      setLoadError(error?.message || "Failed to create share link.");
      showActionToast(error?.message || "Failed to create share link.", "error");
    }
  }, [handleCloseRowMenu, rowMenuCandidate, showActionToast]);

  const handleAddNotesAction = useCallback(async () => {
    const candidate = rowMenuCandidate;
    handleCloseRowMenu();
    if (!candidate) return;

    const preferences = getCandidatePreferences(candidate);
    const currentNotes = pickFirst(candidate?.notes, preferences?.notes);
    const nextNotes = window.prompt("Add or update notes for this candidate:", currentNotes);
    if (nextNotes === null) return;

    const trimmedNotes = cleanText(nextNotes);
    try {
      await saveCandidateRowUpdate(candidate, {
        notes: trimmedNotes,
        preferences_json: stringifyCandidatePreferences({
          ...preferences,
          notes: trimmedNotes,
        }),
      });
      showActionToast(
        trimmedNotes ? "Candidate notes updated." : "Candidate notes cleared."
      );
    } catch (err) {
      console.error("Failed to update candidate notes:", err);
      setLoadError(err?.message || "Failed to update candidate notes.");
      showActionToast(err?.message || "Failed to update candidate notes.", "error");
    }
  }, [rowMenuCandidate, handleCloseRowMenu, saveCandidateRowUpdate, showActionToast]);

  const handleAddTagsAction = useCallback(async () => {
    const candidate = rowMenuCandidate;
    handleCloseRowMenu();
    if (!candidate) return;

    const preferences = getCandidatePreferences(candidate);
    const currentTags = extractCandidateExplicitTags(candidate).join(", ");
    const nextTags = window.prompt("Add or update tags (comma-separated):", currentTags);
    if (nextTags === null) return;

    const tagList = uniqueSorted(asArray(nextTags));
    try {
      await saveCandidateRowUpdate(candidate, {
        tags: tagList,
        candidate_tags: tagList,
        preferences_json: stringifyCandidatePreferences({
          ...preferences,
          tags: tagList,
          tagList,
        }),
      });
      showActionToast(
        tagList.length ? "Candidate tags updated." : "Candidate tags cleared."
      );
    } catch (err) {
      console.error("Failed to update candidate tags:", err);
      setLoadError(err?.message || "Failed to update candidate tags.");
      showActionToast(err?.message || "Failed to update candidate tags.", "error");
    }
  }, [rowMenuCandidate, handleCloseRowMenu, saveCandidateRowUpdate, showActionToast]);

  const handleShortlistAction = useCallback(async () => {
    const candidate = rowMenuCandidate;
    handleCloseRowMenu();
    if (!candidate) return;

    const preferences = getCandidatePreferences(candidate);
    const nextTags = uniqueSorted([...extractCandidateExplicitTags(candidate), "Shortlist"]);
    const alreadyShortlisted = extractCandidateExplicitTags(candidate).some(
      (tag) => normalizeFilterTerm(tag) === "shortlist"
    );
    if (alreadyShortlisted) {
      showActionToast("Candidate is already on the shortlist.", "info");
      return;
    }

    try {
      await saveCandidateRowUpdate(candidate, {
        tags: nextTags,
        candidate_tags: nextTags,
        preferences_json: stringifyCandidatePreferences({
          ...preferences,
          tags: nextTags,
          tagList: nextTags,
        }),
      });
      showActionToast("Candidate added to shortlist.");
    } catch (err) {
      console.error("Failed to shortlist candidate:", err);
      setLoadError(err?.message || "Failed to shortlist candidate.");
      showActionToast(err?.message || "Failed to shortlist candidate.", "error");
    }
  }, [rowMenuCandidate, handleCloseRowMenu, saveCandidateRowUpdate, showActionToast]);

  const handleArchiveCandidateAction = useCallback(async () => {
    const candidate = rowMenuCandidate;
    handleCloseRowMenu();
    if (!candidate) return;

    const confirmArchive = window.confirm(
      `Archive ${candidate?.candidate_name || candidate?.full_name || "this candidate"}?`
    );
    if (!confirmArchive) return;

    try {
      await saveCandidateRowUpdate(candidate, {
        status: "Not Available",
        candidate_status: "Not Available",
      });
      showActionToast("Candidate archived.");
    } catch (err) {
      console.error("Failed to archive candidate:", err);
      setLoadError(err?.message || "Failed to archive candidate.");
      showActionToast(err?.message || "Failed to archive candidate.", "error");
    }
  }, [rowMenuCandidate, handleCloseRowMenu, saveCandidateRowUpdate, showActionToast]);

  const applyStatusLocally = useCallback((ids, status) => {
    const idSet = new Set((ids || []).map((id) => String(id)));

    setCandidates((prev) =>
      prev.map((row) =>
        idSet.has(String(row?.candidate_id || row?.id))
          ? { ...row, status, candidate_status: status }
          : row
      )
    );

    setSelectedCandidate((prev) => {
      if (!prev) return prev;
      if (!idSet.has(String(prev?.candidate_id || prev?.id))) return prev;
      return { ...prev, status, candidate_status: status };
    });
  }, []);

  const handleApplyStatus = useCallback(async (status) => {
    const nextStatus = String(status || "").trim();
    if (!nextStatus || !selectedCount || statusSaving) return;

    const candidateIds = selectedCandidates
      .map((candidate) => candidate?.candidate_id || candidate?.id)
      .filter(Boolean);

    if (!candidateIds.length) return;

    const base = normalizeApiBase(API_BASE);
    const endpoints = [
      `${base}/api/candidates/bulk-status`,
      `${base}/candidates/bulk-status`,
    ];

    setStatusSaving(true);
    setLoadError("");

    let lastError = "";

    try {
      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, {
            method: "PATCH",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
            body: JSON.stringify({
              ids: candidateIds,
              status: nextStatus,
            }),
          });

          if (!res.ok) {
            const text = await res.text().catch(() => "");
            lastError = `Failed to update candidate status (${res.status}). ${text.slice(0, 200)}`;
            continue;
          }

          const data = await res.json().catch(() => ({}));
          const appliedStatus = pickFirst(data?.status, nextStatus) || nextStatus;
          const updatedIds = Array.isArray(data?.updated_ids) && data.updated_ids.length
            ? data.updated_ids
            : candidateIds;

          applyStatusLocally(updatedIds, appliedStatus);
          setSelectedIds([]);
          // Reconcile with the persisted rows without replacing the table
          // with the page-level loading state.
          await fetchCandidatesOnly({ showLoading: false });
          setStatusToast({
            open: true,
            severity: "success",
            message: `Updated ${updatedIds.length} candidate${updatedIds.length === 1 ? "" : "s"} to "${appliedStatus}".`,
          });
          return;
        } catch (err) {
          lastError = err?.message || "Failed to update candidate status";
        }
      }

      throw new Error(lastError || "Failed to update candidate status");
    } catch (err) {
      console.error("Failed to update candidate statuses:", err);
      setLoadError(err?.message || "Failed to update candidate status");
      setStatusToast({
        open: true,
        severity: "error",
        message: err?.message || "Failed to update candidate status",
      });
    } finally {
      setStatusSaving(false);
    }
  }, [
    selectedCount,
    statusSaving,
    selectedCandidates,
    applyStatusLocally,
    fetchCandidatesOnly,
  ]);

  const closeStatusToast = useCallback(() => {
    setStatusToast((prev) => ({ ...prev, open: false }));
  }, []);

  const handleBulkArchive = useCallback(async (options = {}) => {
    if (!selectedCount || bulkActionBusy || deleting || statusSaving) {
      showActionToast("Select candidates first to archive them.", "info");
      return;
    }

    if (!options?.confirmedByBulkActionsBar) {
      const confirmArchive = window.confirm(
        `Archive ${selectedCount} selected candidate${selectedCount === 1 ? "" : "s"}?`
      );
      if (!confirmArchive) return;
    }

    // Archive uses the candidate profile update route, which supports the
    // archived "Not Available" state. This avoids the legacy bulk-status
    // endpoint that only recognizes a subset of the Candidate status options.
    const result = await applyBulkCandidateUpdates(selectedCandidates, {
      status: "Not Available",
      candidate_status: "Not Available",
    });

    if (result.updatedCount) {
      setSelectedIds([]);
      showActionToast(
        `Archived ${result.updatedCount} candidate${result.updatedCount === 1 ? "" : "s"}.`
      );
    }

    if (result.failedMessages.length) {
      setLoadError(result.failedMessages.join(" | "));
      showActionToast(result.failedMessages[0], "error");
    }
  }, [
    applyBulkCandidateUpdates,
    bulkActionBusy,
    deleting,
    selectedCount,
    selectedCandidates,
    showActionToast,
    statusSaving,
  ]);

  const handleSelectBulkMoreAction = useCallback(
    (value, _event, options = {}) => {
      switch (value) {
        case "assign-sourcer":
          handleAssignSourcer();
          return;
        case "add-tags":
          handleBulkAddTags();
          return;
        case "add-to-template":
          handleAddToTemplate();
          return;
        case "add-to-sequence":
          handleBulkAddToSequence();
          return;
        case "send-email":
          handleSendEmail();
          return;
        case "generate-client-submission":
          handleGenerateClientSubmission();
          return;
        case "export-csv":
          handleExportCsv();
          return;
        case "archive":
          handleBulkArchive(options);
          return;
        default:
      }
    },
    [
      handleAddToTemplate,
      handleAssignSourcer,
      handleBulkAddTags,
      handleBulkArchive,
      handleBulkAddToSequence,
      handleExportCsv,
      handleGenerateClientSubmission,
      handleSendEmail,
    ]
  );

  const topBarActions = useMemo(
    () => [
      { key: "schedule", label: "Schedule", variant: "contained", startIcon: <EventOutlinedIcon />, onClick: () => openInterviewScheduler(), sx: HEADER_ACTION_BUTTON_SX },
      {
        key: "filter",
        label: "Filter By",
        variant: "contained",
        startIcon: <Filter size="16" color="currentColor" variant="Linear" />,
        onClick: handleOpenFilterDrawer,
        sx: HEADER_ACTION_BUTTON_SX,
      },
      {
        key: "import-csv",
        label: "Import CSV",
        variant: "contained",
        onClick: () => setCsvImportOpen(true),
        sx: HEADER_ACTION_BUTTON_SX,
      },
      {
        key: "add-new-candidate",
        label: "Add New Candidate",
        variant: "contained",
        startIcon: <User size="16" color="currentColor" variant="Linear" />,
        onClick: handleOpenAddCandidate,
        sx: HEADER_ACTION_BUTTON_SX,
      },
      {
        key: "launch-agent",
        label: "Launch Agent",
        variant: "contained",
        startIcon: <AgentActionIcon size={16} />,
        onClick: handleOpenLaunchAgentMenu,
        sx: HEADER_ACTION_BUTTON_SX,
      },
    ],
    [handleOpenAddCandidate, handleOpenFilterDrawer, handleOpenLaunchAgentMenu, setCsvImportOpen]
  );

  // For an unfiltered server-paginated view, report the database total rather
  // than the number of rows in the current page. This keeps the tab heading
  // and pagination consistent when the selected page size is 10.
  const countLabel = hasCandidateClientFilters ? filteredCandidates.length : candidateTotal;
  const tableResetSignal = useMemo(
    () => JSON.stringify({ listTab, search, activeStatId, appliedFilters, advancedApplied }),
    [activeStatId, advancedApplied, appliedFilters, listTab, search]
  );

  const handleListTabChange = useCallback(
    (_event, nextTab) => {
      const tab = nextTab === "pipeline" ? "pipeline" : "list";
      const params = new URLSearchParams(location.search);
      if (tab === "pipeline") params.set("view", "pipeline");
      else {
        params.delete("view");
        params.delete("overviewMetric");
        params.delete("stage");
      }
      setListTab(tab);
      setActiveStatId(null);
      setCandidatePage(1);
      setSelectedIds([]);
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : "",
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  const handleStatItemClick = useCallback((item) => {
    setAppliedFilters(createEmptyFilterDraft());
    setFilterDraft(createEmptyFilterDraft());
    setAdvancedApplied(null);
    const nextId = String(item?.id || "");
    if (!nextId) {
      setActiveStatId(null);
      setCandidatePage(1);
      return;
    }
    setActiveStatId((prev) => (prev === nextId ? null : nextId));
    setCandidatePage(1);
  }, []);

  const bulkActionsNode = useMemo(
    () => (
      <BulkActionsBar
        selectedCount={selectedCount}
        selectionLabel="candidate"
        actions={[
          {
            type: "menu",
            label: "Launch Agent",
            options: CANDIDATE_AGENT_TASK_OPTIONS,
            onSelect: handleLaunchCandidateAgentTask,
            disabled: bulkActionBusy || deleting || statusSaving,
            disabledMessage:
              selectedCount === 0 && !deleting && !statusSaving && !bulkActionBusy
                ? "Please select candidates to launch an agent task."
                : "",
          },
          { type: "divider", sx: { width: "1px" } },
          {
            type: "menu",
            label: bulkActionBusy ? "Working..." : "Move Stage",
            options: listTab === "pipeline" ? BULK_STAGE_OPTIONS : CORE_CANDIDATE_STAGES.map(stage => stage.label),
            onSelect: handleBulkMoveStage,
            disabledMessage:
              selectedCount === 0 && !deleting && !statusSaving && !bulkActionBusy
                ? "Please select candidates to move stage."
                : "",
            disabled: selectedCount === 0 || deleting || statusSaving || bulkActionBusy,
          },
          { type: "divider", sx: { width: "1px" } },
          {
            type: "menu",
            label: statusSaving ? "Saving..." : "Change Status",
            options: BULK_STATUS_OPTIONS,
            onSelect: handleApplyStatus,
            feedback: statusToast,
            onFeedbackClose: closeStatusToast,
            disabledMessage:
              selectedCount === 0 && !deleting && !statusSaving
                ? "Please select an item to change status."
                : "",
            disabled: selectedCount === 0 || deleting || statusSaving || bulkActionBusy,
          },
          { type: "divider", sx: { width: "1px" } },
          {
            type: "node",
            key: "assign-recruiter",
            node: (
              <AssignTeamMemberAction
                apiBase={API_BASE}
                selectedCount={selectedCount}
                disabled={bulkActionBusy || deleting || statusSaving}
                label="Assign Recruiter"
                optionsMode="recruiterUsers"
                assignmentRoleLabel="recruiter"
                emptySelectionMessage="Select at least one candidate to assign a recruiter."
                onAssign={handleAssignRecruiter}
              />
            ),
          },
          { type: "divider", sx: { width: "1px" } },
          {
            type: "menu",
            label: "More Actions",
            options: bulkMoreActionOptions,
            onSelect: handleSelectBulkMoreAction,
            disabled: bulkActionBusy || deleting || statusSaving,
            menuWidth: 280,
          },
          { type: "divider", sx: { width: "1px" } },
          {
            type: "button",
            label: deleting ? "Deleting..." : "Delete",
            onClick: handleDeleteSelected,
            disabled: selectedCount === 0 || deleting || statusSaving || bulkActionBusy,
          },
        ]}
        rightButtons={[
          {
            key: "add-to-job",
            label: "Add to Job",
            confirm: true,
            background: PURPLE,
            color: "#fff",
            onClick: handleAddToJob,
            sx: BULK_ACTION_RIGHT_BUTTON_SX,
          },
          {
            key: "add-to-sequence-pill",
            label: "Add to Sequence",
            confirm: true,
            background: PURPLE,
            color: "#fff",
            onClick: handleBulkAddToSequence,
            sx: BULK_ACTION_RIGHT_BUTTON_SX,
          },
          {
            key: "match-to-job",
            label: "Match to Job",
            confirm: true,
            background: PURPLE,
            color: "#fff",
            onClick: handleMatchToJob,
            sx: BULK_ACTION_RIGHT_BUTTON_SX,
          },
        ]}
      />
    ),
    [
      bulkActionBusy,
      listTab,
      handleAddToJob,
      handleAssignRecruiter,
      handleBulkAddToSequence,
      handleBulkMoveStage,
      handleDeleteSelected,
      handleApplyStatus,
      handleLaunchCandidateAgentTask,
      handleMatchToJob,
      handleSelectBulkMoreAction,
      bulkMoreActionOptions,
      closeStatusToast,
      selectedCount,
      deleting,
      statusSaving,
      statusToast,
    ]
  );

  if (showAdvancedSearch) {
    return (
      <Box sx={{ py: 0, pb: 3, border: 0 }}>
        <Breadcrumb
          items={[
            { label: "Overview", onClick: () => navigate("/overview") },
            { label: "Candidates", path: "/candidates" },
            { label: "Advance Search" },
          ]}
        />

        <Box>
          <CandidateAdvancedSearchView
            title="Candidates - Advance Search"
            initialFilters={mergeBasicIntoAdvancedCandidateFilters(appliedFilters, advancedApplied)}
            options={advancedSearchOptions}
            onClose={handleCloseAdvancedSearch}
            onApply={handleApplyAdvancedSearch}
          />
        </Box>
      </Box>
    );
  }

  if (requestedCandidateProfileId || viewMode === "candidateTabsLayout") {
    if (!selectedCandidate || (requestedCandidateProfileId &&
      String(selectedCandidate.candidate_id || selectedCandidate.id) !== String(requestedCandidateProfileId))) {
      return (
        <Box sx={{ py: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography sx={{ fontWeight: 800 }}>
              Loading candidate profile...
            </Typography>
          </Stack>
        </Box>
      );
    }

    return (
      <CandidateTabsLayout
        candidate={selectedCandidate}
        detailFieldOptions={candidateDetailFieldOptions}
        initialTab={requestedCandidateProfileId ? normalizeTab(new URLSearchParams(location.search).get("tab")) : defaultTab}
        onBack={() => {
          setShowAdvancedSearch(false);
          setCandidateDrawerOpen(false);
          setCandidateDrawerLoading(false);
          setFilterDrawerOpen(false);
          setBulkActionBusy(false);
          setIsAddCandidateOpen(false);
          setIsAgentDialogOpen(false);
          setAgentDialogInitialPrompt("");
          setAgentDialogSubmitOnOpen(false);
          setIsAddToSequenceOpen(false);
          setSequenceDialogItemsOverride(null);
          setRowMenuAnchorEl(null);
          setRowMenuCandidate(null);
          setViewMode("list");
          setSelectedCandidate(null);

          navigate(
            {
              pathname: location.pathname,
              search: "",
            },
            {
              replace: true,
              state: createSidebarResetState(location.pathname),
            }
          );
        }}
      />
    );
  }

  return (
    <Box sx={{ py: 0, pb: 3, border: 0 }}>
      {!!loadError && (
        <Box sx={{ pb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}

      <ListTableTabsTemplate
        breadcrumb={
          <Breadcrumb
            items={[
              { label: "Overview", onClick: () => navigate("/overview") },
              { label: "Candidates", path: "/candidates" },
            ]}
          />
        }
        statsSection={
          <StatsSlider
            items={topStats}
            autoPlay={false}
            showArrows={true}
            cardMinWidth={listTab === "list" ? 160 : 200}
            gap={12}
            onItemClick={handleStatItemClick}
            activeItemId={activeStatId}
          />
        }
        contentSection={
          <>
            <Card
              sx={{
                p: 0,
                backgroundColor: "#fff",
                boxShadow: "none",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <TopListHeaderBar
                title={listTab === "pipeline" ? "Recruiting Pipeline" : "Candidates"}
                searchPlaceholder="Search..."
                searchValue={search}
                onSearchChange={(e) => setSearch(e.target.value)}
                actions={topBarActions}
                countLabel={`Results: ${countLabel}`}
                resultsToolbarTargetId="candidates-results-toolbar"
                tabContent={
                  <Box sx={{ px: { xs: 0, sm: 0 }, pb: 1.5, mb: "-2px" }}>
                    <Tabs
                      value={listTab}
                      onChange={handleListTabChange}
                      aria-label="Candidate views"
                      sx={{
                        minHeight: 38,
                        borderBottom: `1px solid ${BORDER}`,
                        overflow: "visible",
                        "& .MuiTab-root": {
                          minHeight: 38,
                          px: 2,
                          textTransform: "none",
                          fontSize: 14,
                          fontWeight: 400,
                          color: PURPLE,
                        },
                        "& .Mui-selected": { color: `${PURPLE} !important` },
                        "& .MuiTabs-indicator": { bgcolor: BLUE, height: 3, bottom: "2px", zIndex: 1 },
                      }}
                    >
                      <Tab
                        value="list"
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <span>Candidates</span>
                            <Box component="span" className="tab-count-badge" sx={{ px: 0.7, py: 0.1, borderRadius: 5, bgcolor: "#F1EBFF", color: PURPLE, fontSize: 14, fontWeight: 400 }}>
                              {candidateListTotal ?? candidateTotal}
                            </Box>
                          </Stack>
                        }
                      />
                      <Tab
                        value="pipeline"
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <span>Recruiting Pipeline</span>
                            <Box component="span" className="tab-count-badge" sx={{ px: 0.7, py: 0.1, borderRadius: 5, bgcolor: "#F1EBFF", color: PURPLE, fontSize: 14, fontWeight: 400 }}>
                              {recruitingPipelineCount}
                            </Box>
                          </Stack>
                        }
                      />
                    </Tabs>
                  </Box>
                }
              />

              {(loading || deleting) && listTab !== "pipeline" ? (
                <Box sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography sx={{ fontWeight: 700 }}>
                      {deleting ? "Deleting selected candidates..." : "Loading candidates..."}
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <RecruitingPipelineTable
                  enabled={listTab === "pipeline"}
                  scopeKey={JSON.stringify([activeStatId, search, appliedFilters, advancedApplied])}
                  request={(url, options = {}) => fetch(url, { ...options, credentials: "include", headers: { ...options.headers, ...authHeaders() } })}
                  apiBase={normalizeApiBase(API_BASE)}
                  user={user}
                  loading={loading || deleting}
                  onMoved={() => fetchCandidatesOnly({ showLoading: false })}
                  columns={columns}
                  exportFields={[...columns, ...CANDIDATE_EXPORT_TAB_FIELDS].filter((field) => field.id !== "actions")}
                  rows={filteredCandidates}
                  renderRow={renderRow}
                  defaultOrderBy="last_activity_at"
                  defaultOrderDirection="desc"
                  resetPageSignal={tableResetSignal}
                  includeSelectionColumn={true}
                  fitToContainer
                  resultsToolbarTargetId="candidates-results-toolbar"
                  visualVariant="company-standard"
                  serverSidePagination={!hasCandidateClientFilters}
                  resultsTotalCount={hasCandidateClientFilters ? undefined : candidateTotal}
                  serverPage={candidatePage - 1}
                  serverRowsPerPage={candidateRowsPerPage}
                  onServerPageChange={handleCandidatePageChange}
                  onServerRowsPerPageChange={handleCandidateRowsPerPageChange}
                  initialRowsPerPage={10}
                  resultsFilterPills={resultsFilterPills}
                  selectedIds={normalizedSelectedIds}
                  onSelectedIdsChange={(ids) =>
                    setSelectedIds(Array.isArray(ids) ? ids.map((id) => String(id)) : [])
                  }
                  bulkActionsTop={bulkActionsNode}
                  bulkActionsBottom={bulkActionsNode}
                />
              )}
            </Card>

          </>
        }
      />

      <CandidateProfileDrawer
        open={candidateDrawerOpen}
        onClose={() => setCandidateDrawerOpen(false)}
        candidate={selectedCandidate}
        loading={candidateDrawerLoading}
        onShowMore={handleShowMoreCandidate}
      />

      <CandidateFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        endpoint={filterEndpoint}
        draft={filterDraft}
        setDraft={setFilterDraft}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onAdvancedSearch={handleAdvanceSearch}
      />

      <Menu
        anchorEl={launchAgentMenuAnchorEl}
        open={Boolean(launchAgentMenuAnchorEl)}
        onClose={handleCloseLaunchAgentMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            minWidth: 250,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
          },
        }}
      >
        {CANDIDATE_AGENT_TASK_OPTIONS.map((task) => (
          <MenuItem
            key={task.value}
            onClick={() => {
              handleCloseLaunchAgentMenu();
              handleLaunchCandidateListAgentTask(task.value);
            }}
            sx={{ fontSize: 13, py: 1 }}
          >
            {task.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
          },
        }}
      >
        <MenuItem onClick={handleViewProfileAction} sx={{ fontSize: 13 }}>
          View Profile
        </MenuItem>
        <MenuItem onClick={handleEditCandidateAction} sx={{ fontSize: 13 }}>
          Edit Candidate
        </MenuItem>
        <MenuItem onClick={handleShareCandidateAction} sx={{ fontSize: 13 }}>
          Share Link
        </MenuItem>
        <MenuItem onClick={handleAddNotesAction} sx={{ fontSize: 13 }}>
          Add Notes
        </MenuItem>
        <MenuItem onClick={handleAddTagsAction} sx={{ fontSize: 13 }}>
          Add Tags
        </MenuItem>
        <MenuItem onClick={handleShortlistAction} sx={{ fontSize: 13 }}>
          Add to Shortlist
        </MenuItem>
        <MenuItem onClick={handleArchiveCandidateAction} sx={{ fontSize: 13 }}>
          Archive Candidate
        </MenuItem>
      </Menu>

      <AddCandidateDialog
        open={isAddCandidateOpen}
        onClose={handleCloseAddCandidate}
        onCreated={handleCandidateCreated}
        user={user}
        apiBase={API_BASE}
      />

      <AIAgentDialog
        open={isAgentDialogOpen}
        onClose={handleCloseAgentDialog}
        user={user}
        initialPrompt={agentDialogInitialPrompt}
        submitOnOpen={agentDialogSubmitOnOpen}
        onActivity={handleCandidateAgentActivity}
      />
      <CsvImportDialog open={csvImportOpen} onClose={() => setCsvImportOpen(false)} entityLabel="candidates" importEndpoint={`${API_BASE}/api/csv-import/candidates`} onImported={handleCandidateCreated} />

      <CandidatePitchPackageDialog
        open={candidatePitchPackageDialog.open}
        candidates={candidatePitchPackageDialog.candidates}
        onClose={() => setCandidatePitchPackageDialog({ open: false, candidates: [] })}
        onCreate={handleCandidatePitchPackageCreated}
      />

      <AddToSequenceDialog
        open={isAddToSequenceOpen}
        onClose={handleCloseAddToSequence}
        entityType="candidate"
        items={sequenceDialogItems}
        recommendedItems={recommendedSequenceItems}
        user={user}
        initialCampaignType={sequenceDialogInitialCampaignType}
        initialScreen={sequenceDialogInitialCampaignType === "sales" ? "audience" : "start"}
        onCreated={() => {
          setSelectedIds([]);
          setSequenceDialogItemsOverride(null);
          setSequenceDialogInitialCampaignType("recruiting");
        }}
      />

      <CandidateResumePreviewDialog
        resume={resumePreview}
        onClose={() => setResumePreview(null)}
      />

      <Snackbar
        open={actionToast.open}
        autoHideDuration={3200}
        onClose={closeActionToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeActionToast}
          severity={actionToast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {actionToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const Candidates = () => {
  const { user, loading } = useAuthController();

  if (loading) return <p>Loading...</p>;

  return <CandidatesContent user={user} />;
};

export default Candidates;
